import React, { useState, useEffect, useCallback, useRef } from 'react';
import { MapContainer, TileLayer, Marker, Polyline, useMapEvents, useMap, CircleMarker, Tooltip } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { useAuth } from '@/contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { Clock, Check, MapPin, Phone, Lightning, Warning, CheckCircle, ArrowLeft, Target, ArrowClockwise, WifiHigh, WifiSlash, CloudArrowUp, Database, Plus, CurrencyDollar, ShoppingCart, User, House, CalendarBlank, MagnifyingGlass, Funnel, CaretDown, Eye, EyeSlash, X, SignOut } from '@phosphor-icons/react';
import { toast } from 'sonner';
import { initDB, saveTasksLocal, getLocalTasks, updateLocalTask, addToSyncQueue, getSyncQueue, setCache, getCache } from '@/lib/offlineDB';
import { processSync, isOnline, startAutoSync, stopAutoSync } from '@/lib/syncEngine';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const APP_VERSION = '1.0.4';
const BUILD_NUMBER = '4';

// Fix Leaflet default icon issue with webpack
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png',
  iconUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png',
  shadowUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png',
});

function MapClickHandler({ mapAddMode, mapDrawPoints, setMapDrawPoints, setMapFormPos, setMapFormVisible }) {
  const map = useMapEvents({
    click(e) {
      if (!mapAddMode) return;
      const { lat, lng } = e.latlng;
      if (mapAddMode === 'cable_route') {
        setMapDrawPoints(prev => [...prev, { lat, lng }]);
      } else {
        setMapFormPos({ lat, lng });
        setMapFormVisible(true);
      }
    }
  });
  return null;
}

// Component to render KMZ routes using native Leaflet API (bypasses react-leaflet SVG clamp issue)
function NativePolylines({ routes, visible }) {
  const map = useMap();
  const layerGroupRef = React.useRef(null);
  
  React.useEffect(() => {
    console.log('[NativePolylines] effect: map=', !!map, 'visible=', visible, 'routes=', routes?.length);
    if (!map) return;
    
    // Remove existing layer group
    if (layerGroupRef.current) {
      layerGroupRef.current.clearLayers();
      map.removeLayer(layerGroupRef.current);
      layerGroupRef.current = null;
    }
    
    if (!visible || !routes || routes.length === 0) {
      console.log('[NativePolylines] skipping: visible=', visible, 'routes=', routes?.length);
      return;
    }
    
    const lg = L.layerGroup();
    routes.forEach(r => {
      const rawPts = r.points || [];
      const pts = rawPts.map(p => Array.isArray(p) ? L.latLng(p[0], p[1]) : L.latLng(p.lat, p.lng));
      if (pts.length < 2) return;
      const color = r.color || (r.obj_type === 'backbone_route' ? '#ff0000' : '#ffff00');
      const weight = r.width || (r.obj_type === 'backbone_route' ? 4 : 3);
      
      // Check if it's a closed polygon (first == last point)
      const first = rawPts[0];
      const last = rawPts[rawPts.length - 1];
      const isClosed = Array.isArray(first) && Array.isArray(last) &&
        Math.abs(first[0] - last[0]) < 0.00001 && Math.abs(first[1] - last[1]) < 0.00001;
      
      let layer;
      if (isClosed && pts.length > 3) {
        // Render as polygon (coverage area)
        layer = L.polygon(pts, { color, weight, opacity: 0.9, fillColor: color, fillOpacity: 0.15 });
      } else {
        // Render as polyline (cable route)
        layer = L.polyline(pts, { color, weight, opacity: 0.9 });
      }
      layer.bindTooltip(r.name, { sticky: true, direction: 'top' });
      lg.addLayer(layer);
    });
    
    console.log('[NativePolylines] adding', lg.getLayers().length, 'layers to map');
    lg.addTo(map);
    layerGroupRef.current = lg;
    
    return () => {
      if (layerGroupRef.current) {
        layerGroupRef.current.clearLayers();
        map.removeLayer(layerGroupRef.current);
        layerGroupRef.current = null;
      }
    };
  }, [routes, visible, map]);
  
  return null;
}

function FitBoundsOnLoad({ routes }) {
  const map = useMapEvents({});
  React.useEffect(() => {
    if (!routes || routes.length === 0) return;
    const allPoints = [];
    routes.forEach(r => {
      const pts = r.points || [];
      pts.forEach(p => {
        if (Array.isArray(p) && p.length >= 2) allPoints.push(p);
        else if (p && p.lat && p.lng) allPoints.push([p.lat, p.lng]);
      });
    });
    if (allPoints.length < 2) return;
    try {
      const bounds = L.latLngBounds(allPoints);
      if (bounds.isValid()) {
        map.fitBounds(bounds, { padding: [30, 30], maxZoom: 15 });
      }
    } catch(e) { /* ignore */ }
  }, [routes, map]);
  return null;
}

export default function MobileApp() {
  const { user, login, logout } = useAuth();
  const navigate = useNavigate();
  // ALL hooks must be declared before any conditional returns
  const [screen, setScreen] = useState('home');
  React.useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    const frame = window.requestAnimationFrame(() => window.scrollTo(0, 0));
    return () => window.cancelAnimationFrame(frame);
  }, [screen]);
  const [tasks, setTasks] = useState([]);
  const remoteLog = async (msg, extra) => {
    try {
      const tok = localStorage.getItem("session_token") || "none";
      await fetch(API + "/debug-log", {
        method: "POST",
        headers: {"Content-Type":"application/json"},
        body: JSON.stringify(Object.assign({msg, tok: tok.slice(0,20), ts: new Date().toISOString()}, extra || {}))
      });
    } catch(e) {}
  };
  const [stats, setStats] = useState({});
  const [target, setTarget] = useState({});
  const [selectedTask, setSelectedTask] = useState(null);
  const [reportForm, setReportForm] = useState({ work_notes: '', power_reading: '', cust_phone: '', house_no: '', street: '' });
  const [loading, setLoading] = useState(false);
  const [online, setOnline] = useState(navigator.onLine);
  const [syncStatus, setSyncStatus] = useState('synced');
  const [pendingCount, setPendingCount] = useState(0);
  const [syncing, setSyncing] = useState(false);
  const [dbReady, setDbReady] = useState(false);
  const [lastSync, setLastSync] = useState(null);
  const [updateInfo, setUpdateInfo] = useState(null);
  const [updateDismissed, setUpdateDismissed] = useState(false);
  const [checkingUpdate, setCheckingUpdate] = useState(false);
  // Login state
  const [loginStaffId, setLoginStaffId] = useState('');
  const [loginPassword, setLoginPassword] = useState('');
  const [loginDept, setLoginDept] = useState('IMFT');
  const [loginBrand, setLoginBrand] = useState('MyanmarLink');
  const [showLoginPw, setShowLoginPw] = useState(false);
  const [loginLoading, setLoginLoading] = useState(false);
  const [loginError, setLoginError] = useState('');

  const [gpsStatus, setGpsStatus] = useState(null);
  const [gettingGPS, setGettingGPS] = useState(false);
  const [photos, setPhotos] = useState([]);
  const [signalData, setSignalData] = useState({ olt_port: "", onu_serial: "", rx_level: "", tx_level: "" });
  // PWA Install
  const [installPrompt, setInstallPrompt] = useState(null);
  const [isInstalled, setIsInstalled] = useState(false);

  // BMFT-specific state
  const [bmftReport, setBmftReport] = useState({ work_notes: '', error_type: '', fat_name: '', fat_location: '', fiber_meter_used: '', onu_sn: '', remark: '' });
  const [bmftProgress, setBmftProgress] = useState(0);
  const [bmftOnsiteNote, setBmftOnsiteNote] = useState('');
  const [bmftSubmitting, setBmftSubmitting] = useState(false);
  const [bmftKpi, setBmftKpi] = useState({ services_today: 0, installs_today: 0, services_bonus: 0, installs_bonus: 0, total_bonus: 0, kpi_pct: 0 });
  const [bmftMonthly, setBmftMonthly] = useState({ total_jobs: 0, completed: 0, fault_history: [], services_count: 0, installs_count: 0, total_bonus: 0 });
  const [bmftSchedule, setBmftSchedule] = useState({ checkin_time: null, checkout_time: null, today_jobs: [], next_job: null });

  // Map state
  const [mapObjects, setMapObjects] = useState([]);
  const [mapLoading, setMapLoading] = useState(false);
  const [mapAddMode, setMapAddMode] = useState(null); // 'odb' | 'cable_route' | 'user_home' | null
  const [mapDrawPoints, setMapDrawPoints] = useState([]); // for cable route drawing
  const [mapForm, setMapForm] = useState({ name: '', description: '', capacity: 16 });
  const [mapFormVisible, setMapFormVisible] = useState(false);
  const [mapFormPos, setMapFormPos] = useState(null); // { lat, lng }
  const [mapSelectedObj, setMapSelectedObj] = useState(null);
  const [mapFilter, setMapFilter] = useState({ odb: true, cable_route: true, user_home: true });
  const [kmzVisible, setKmzVisible] = useState(true); // KMZ infrastructure layer toggle
  const [userMapLocation, setUserMapLocation] = useState(null);
  const [boxSearchQuery, setBoxSearchQuery] = useState('');
  const [boxSearchResults, setBoxSearchResults] = useState([]);
  const [routeTarget, setRouteTarget] = useState(null);
  const geoWatchRef = useRef(null);

  // Tracking state
  const [trackingActive, setTrackingActive] = useState(false);
  const [trackingPings, setTrackingPings] = useState([]);
  const trackingIntervalRef = useRef(null);
  const [odbCheckQuery, setOdbCheckQuery] = useState('');
  const [odbCheck, setOdbCheck] = useState(null);
  const [odbChecking, setOdbChecking] = useState(false);
  const [teamMovement, setTeamMovement] = useState([]);
  const [teamMovementLoading, setTeamMovementLoading] = useState(false);

  // FTTH Sales State
  const [salesRecords, setSalesRecords] = useState([]);
  const [salesStats, setSalesStats] = useState({});
  const [salesLoading, setSalesLoading] = useState(false);
  const [showNewSaleForm, setShowNewSaleForm] = useState(false);
  const [salesFilter, setSalesFilter] = useState('all');
  const [selectedSale, setSelectedSale] = useState(null);
  const [newSale, setNewSale] = useState({
    customer_name: '', phone: '', township: '', address: '', plan_name: '', plan_speed: '',
    price: '', currency: 'MMK', team_type: 'FTTH', notes: ''
  });

  // Register Service Worker + capture install prompt
  useEffect(() => {
    // Service Worker
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.register('/sw.js').then(() => console.log('SW registered')).catch(() => {});
    }
    // PWA install prompt
    const handler = (e) => { e.preventDefault(); setInstallPrompt(e); };
    window.addEventListener('beforeinstallprompt', handler);
    // Check if already installed
    if (window.matchMedia('(display-mode: standalone)').matches) setIsInstalled(true);
    return () => window.removeEventListener('beforeinstallprompt', handler);
  }, []);
  // Request GPS permission immediately on app load - triggers browser permission dialog
  useEffect(() => {
    if (!navigator.geolocation) return;
    // Use Permissions API to check status first if available
    if (navigator.permissions) {
      navigator.permissions.query({ name: 'geolocation' }).then((result) => {
        if (result.state === 'prompt') {
          // Permission not yet decided - request it now to show dialog
          navigator.geolocation.getCurrentPosition(
            (pos) => setUserMapLocation({ lat: pos.coords.latitude, lng: pos.coords.longitude }),
            () => {}, // silent fail - user denied
            { enableHighAccuracy: true, timeout: 10000, maximumAge: 60000 }
          );
        } else if (result.state === 'granted') {
          // Already granted - get location immediately
          navigator.geolocation.getCurrentPosition(
            (pos) => setUserMapLocation({ lat: pos.coords.latitude, lng: pos.coords.longitude }),
            () => {},
            { enableHighAccuracy: true, timeout: 10000, maximumAge: 60000 }
          );
        }
        // If 'denied', do nothing - user must change in settings
      }).catch(() => {
        // Permissions API not available - just try directly
        navigator.geolocation.getCurrentPosition(
          (pos) => setUserMapLocation({ lat: pos.coords.latitude, lng: pos.coords.longitude }),
          () => {},
          { enableHighAccuracy: true, timeout: 10000, maximumAge: 60000 }
        );
      });
    } else {
      // No Permissions API - request directly (will show dialog on first call)
      navigator.geolocation.getCurrentPosition(
        (pos) => setUserMapLocation({ lat: pos.coords.latitude, lng: pos.coords.longitude }),
        () => {},
        { enableHighAccuracy: true, timeout: 10000, maximumAge: 60000 }
      );
    }
  }, []); // Run once on mount

  const handleInstall = async () => {
    if (!installPrompt) return;
    installPrompt.prompt();
    const result = await installPrompt.userChoice;
    if (result.outcome === 'accepted') { setIsInstalled(true); toast.success('App installed!'); }
    setInstallPrompt(null);
  };

  // If not logged in, render mobile login screen instead of main app
  const handleMobileLogin = async (sid, pw, dept) => {
    setLoginLoading(true); setLoginError('');
    try {
      const res = await fetch(`${API}/auth/login`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ staff_id: sid || loginStaffId, password: pw || loginPassword, department: dept || loginDept }) });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Login failed');
      if (data.session_token) localStorage.setItem('session_token', data.session_token);
      login(data);
      remoteLog("DIAG_LOGIN_OK", {name: data.name, dept: data.department, tok_len: data.session_token ? data.session_token.length : 0});
      setTimeout(() => loadFromServer(), 300);
    } catch (err) { setLoginError(err.message); }
    finally { setLoginLoading(false); }
  };

  // Initialize offline DB
  useEffect(() => {
    initDB().then(() => {
      setDbReady(true);
      loadFromLocal();
    }).catch(err => console.error('DB init failed:', err));
    // Online/offline listeners
    const goOnline = () => { setOnline(true); toast.success('Back online — syncing...'); };
    const goOffline = () => { setOnline(false); toast('Working offline', { icon: '📴' }); };
    window.addEventListener('online', goOnline);
    window.addEventListener('offline', goOffline);
    // OTA Update Check on launch
    checkOTAUpdate();
    return () => { window.removeEventListener('online', goOnline); window.removeEventListener('offline', goOffline); stopAutoSync(); };
  }, []);

  // Start auto-sync when DB ready
  useEffect(() => {
    if (!dbReady) return;
    startAutoSync((result) => {
      if (result.synced > 0) { toast.success(`Synced ${result.synced} items`); loadFromServer(); }
      updateSyncStatus();
    });
  }, [dbReady]);

  // Update sync status badge
  const updateSyncStatus = useCallback(async () => {
    if (!dbReady) return;
    const queue = await getSyncQueue();
    setPendingCount(queue.length);
    setSyncStatus(queue.length === 0 ? 'synced' : 'pending');
  }, [dbReady]);

  useEffect(() => { updateSyncStatus(); }, [tasks, updateSyncStatus]);

  // Load from local DB (instant, works offline)
  const loadFromLocal = async () => {
    const localTasks = await getLocalTasks();
    if (localTasks.length > 0) setTasks(localTasks);
    const cachedStats = await getCache('dashboard_stats');
    if (cachedStats) setStats(cachedStats);
    const cachedTarget = await getCache('daily_target');
    if (cachedTarget) setTarget(cachedTarget);
  };

  // Load from server (when online)
  const loadFromServer = async () => {
    if (!navigator.onLine) { remoteLog("DIAG_OFFLINE"); return; }
    try {
      const _tok = localStorage.getItem("session_token");
      remoteLog("DIAG_START", {tok_len: _tok ? _tok.length : 0, user_dept: user ? user.department : "NULL"});
      const today = new Date().toISOString().split('T')[0];
      const storedUser = localStorage.getItem('user');
      const dept = user && user.department ? user.department : (storedUser ? JSON.parse(storedUser).department : 'IMFT');
      const isBMFT = dept === 'BMFT';
      const tasksUrl = isBMFT ? `${API}/bmft/tasks` : `${API}/imft/tasks`;
      const statsUrl = isBMFT ? `${API}/bmft/dashboard-stats` : `${API}/imft/dashboard-stats`;
      const dtUrl = isBMFT ? null : `${API}/imft/daily-target?date=${today}`;
      const token = localStorage.getItem('session_token');
      const authHeaders = { 'Authorization': `Bearer ${token}` };
      const fetches = [fetch(tasksUrl, { headers: authHeaders }), fetch(statsUrl, { headers: authHeaders })];
      if (dtUrl) fetches.push(fetch(dtUrl, { headers: authHeaders }));
      if (isBMFT) {
        fetches.push(fetch(`${API}/bmft/daily-kpi`, { headers: authHeaders }));
        fetches.push(fetch(`${API}/bmft/monthly-summary`, { headers: authHeaders }));
        fetches.push(fetch(`${API}/bmft/today-schedule`, { headers: authHeaders }));
      }
      const results = await Promise.all(fetches);
      const [tRes, sRes, dtRes, kpiRes, monthlyRes, scheduleRes] = results;
      remoteLog("DIAG_TRES", {status: tRes.status, ok: tRes.ok, url: tasksUrl});
      if (tRes.ok) {
        const serverTasks = await tRes.json();
        remoteLog("DIAG_TASKS", {count: serverTasks.length});
        setTasks(serverTasks);
        await saveTasksLocal(serverTasks); // Cache locally
      }
      if (sRes.ok) { const d = await sRes.json(); setStats(d); await setCache('dashboard_stats', d); }
      if (dtRes && dtRes.ok) { const d = await dtRes.json(); setTarget(d); await setCache('daily_target', d); }
      if (kpiRes && kpiRes.ok) { const d = await kpiRes.json(); setBmftKpi(d); }
      if (monthlyRes && monthlyRes.ok) { const d = await monthlyRes.json(); setBmftMonthly(d); }
      if (scheduleRes && scheduleRes.ok) { const d = await scheduleRes.json(); setBmftSchedule(d); }
      setLastSync(new Date().toISOString());
    } catch (err) {
      remoteLog("DIAG_CATCH", {err: String(err)});
      console.warn('Server fetch failed, using local data', err);
    }
  };

  useEffect(() => { if (dbReady && online && user) loadFromServer(); }, [dbReady, online, user]);

  // ===== TRACKING FUNCTIONS (must be before conditional return) =====
  const sendTrackingPing = useCallback(async (activity = 'idle') => {
    if (!navigator.geolocation) return;
    try {
      const pos = await new Promise((res, rej) => navigator.geolocation.getCurrentPosition(res, rej, { timeout: 8000, maximumAge: 30000 }));
      const token = localStorage.getItem('session_token');
      const battery = navigator.getBattery ? await navigator.getBattery().then(b => Math.round(b.level * 100)).catch(() => null) : null;
      const ping = { lat: pos.coords.latitude, lng: pos.coords.longitude, accuracy: Math.round(pos.coords.accuracy), activity, battery, interval_minutes: 10 };
      const res = await fetch(`${API}/tracking/ping`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify(ping)
      });
      if (res.ok) {
        const data = await res.json();
        setTrackingPings(prev => [...prev.slice(-99), { ...ping, ping_id: data.ping_id, timestamp: new Date().toISOString() }]);
      }
    } catch (err) { /* silent fail — offline or GPS denied */ }
  }, []);

  const startTracking = useCallback(() => {
    if (trackingIntervalRef.current) return;
    setTrackingActive(true);
    sendTrackingPing('duty');
    trackingIntervalRef.current = setInterval(() => sendTrackingPing('duty'), 10 * 60 * 1000);
  }, [sendTrackingPing]);

  const stopTracking = useCallback(() => {
    if (trackingIntervalRef.current) { clearInterval(trackingIntervalRef.current); trackingIntervalRef.current = null; }
    setTrackingActive(false);
  }, []);

  const isAdminOrSupervisor = Boolean(user && (
    ['admin', 'supervisor', 'manager'].includes(String(user.role || '').toLowerCase()) ||
    String(user.department || '').toLowerCase() === 'admin'
  ));

  const runOdbCheck = async () => {
    const query = odbCheckQuery.trim();
    if (query.length < 2) return toast.error('Enter at least 2 characters of the ODB/FAT name');
    setOdbChecking(true);
    try {
      const token = localStorage.getItem('session_token');
      const res = await fetch(`${API}/odb-box-search?q=${encodeURIComponent(query)}`, { headers: { 'Authorization': `Bearer ${token}` } });
      const data = await res.json();
      if (!res.ok || !data.ok) throw new Error(data.error || 'ODB check failed');
      setOdbCheck(data);
    } catch (err) { toast.error(err.message || 'ODB check failed'); }
    finally { setOdbChecking(false); }
  };

  const loadTeamMovement = async () => {
    if (!isAdminOrSupervisor) return;
    setTeamMovementLoading(true);
    try {
      const token = localStorage.getItem('session_token');
      const res = await fetch(`${API}/mobile/team-movement`, { headers: { 'Authorization': `Bearer ${token}` } });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Could not load team movement');
      setTeamMovement(Array.isArray(data.staff) ? data.staff : []);
    } catch (err) { toast.error(err.message || 'Could not load team movement'); }
    finally { setTeamMovementLoading(false); }
  };

  // Tracking is staff-controlled and is stopped when the mobile session is closed or the user ends duty.
  useEffect(() => {
    return () => stopTracking();
  }, [stopTracking]);

  // ===== MAP FUNCTIONS (must be before conditional return) =====
  const loadMapObjects = useCallback(async () => {
    setMapLoading(true);
    try {
      const token = localStorage.getItem('session_token');
      const res = await fetch(`${API}/fiber-map/objects`, { headers: { 'Authorization': `Bearer ${token}` } });
      if (res.ok) {
        const data = await res.json();
        const routes = data.filter(o => o.obj_type === 'backbone_route' || o.obj_type === 'distribution_route');
        console.log('[loadMapObjects] total:', data.length, 'routes:', routes.length, 'route names:', routes.map(r => r.name));
        setMapObjects(data);
      }
    } catch (err) { console.error('Map load error:', err); }
    finally { setMapLoading(false); }
  }, []);

  useEffect(() => {
    if (screen === 'map') {
      loadMapObjects();
      if (navigator.geolocation) {
        // Clear any existing watch
        if (geoWatchRef.current !== null) {
          navigator.geolocation.clearWatch(geoWatchRef.current);
        }
        // Start live tracking
        geoWatchRef.current = navigator.geolocation.watchPosition(
          (pos) => {
            setUserMapLocation({ lat: pos.coords.latitude, lng: pos.coords.longitude });
          },
          (err) => {
            // On error, clear and retry after 3 seconds (except permission denied)
            if (err.code !== 1 && geoWatchRef.current !== null) {
              navigator.geolocation.clearWatch(geoWatchRef.current);
              geoWatchRef.current = null;
              setTimeout(() => {
                if (navigator.geolocation) {
                  geoWatchRef.current = navigator.geolocation.watchPosition(
                    (pos) => setUserMapLocation({ lat: pos.coords.latitude, lng: pos.coords.longitude }),
                    () => {},
                    { enableHighAccuracy: false, timeout: 15000, maximumAge: 10000 }
                  );
                }
              }, 3000);
            }
          },
          { enableHighAccuracy: true, timeout: 15000, maximumAge: 5000 }
        );
      }
    } else {
      // Stop tracking when leaving map screen
      if (geoWatchRef.current !== null) {
        navigator.geolocation.clearWatch(geoWatchRef.current);
        geoWatchRef.current = null;
      }
    }
  }, [screen, loadMapObjects]);

  // ===== FTTH Sales Functions =====
  const isSalesRole = user && (user.role === 'Sales Staff' || user.team_name === 'FTTH Sales Team' || user.department === 'Marketing');

  const loadSalesData = async () => {
    if (!navigator.onLine) return;
    setSalesLoading(true);
    try {
      const [salesRes, statsRes] = await Promise.all([
        fetch(`${API}/sales/my`, { headers: { 'Authorization': `Bearer ${localStorage.getItem('session_token')}` } }),
        fetch(`${API}/sales/stats`, { headers: { 'Authorization': `Bearer ${localStorage.getItem('session_token')}` } })
      ]);
      if (salesRes.ok) { const data = await salesRes.json(); setSalesRecords(Array.isArray(data) ? data : []); }
      if (statsRes.ok) { const data = await statsRes.json(); setSalesStats(data); }
    } catch (err) { console.warn('Sales data fetch failed'); }
    finally { setSalesLoading(false); }
  };

  useEffect(() => {
    if (user && isSalesRole && online) { loadSalesData(); }
  }, [user, online]);

  const submitNewSale = async () => {
    if (!newSale.customer_name || !newSale.phone || !newSale.township) {
      toast.error('Please fill in customer name, phone, and township');
      return;
    }
    setSalesLoading(true);
    try {
      const res = await fetch(`${API}/sales`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${localStorage.getItem('session_token')}` },
        body: JSON.stringify({
          ...newSale,
          price: parseFloat(newSale.price) || 0,
        })
      });
      if (!res.ok) { const err = await res.json(); throw new Error(err.detail || 'Failed to create sale'); }
      const data = await res.json();
      toast.success(`Sale ${data.sale_id} created!`);
      setShowNewSaleForm(false);
      setNewSale({ customer_name: '', phone: '', township: '', address: '', plan_name: '', plan_speed: '', price: '', currency: 'MMK', team_type: 'FTTH', notes: '' });
      await loadSalesData();
    } catch (err) { toast.error(err.message); }
    finally { setSalesLoading(false); }
  };

  // OTA Update Check
  const checkOTAUpdate = async () => {
    setCheckingUpdate(true);
    try {
      const res = await fetch(`${API}/ota/check?current_version=${APP_VERSION}&platform=android`);
      if (res.ok) {
        const data = await res.json();
        if (data.update_available) {
          setUpdateInfo(data);
          const dismissed = localStorage.getItem('ota_dismissed') === data.latest_version;
          setUpdateDismissed(dismissed);
          if (data.mandatory && !dismissed) {
            // Force update — block usage
          }
        }
      }
      localStorage.setItem('ota_last_check', new Date().toISOString());
    } catch {}
    finally { setCheckingUpdate(false); }
  };

  const dismissOTAUpdate = () => {
    if (updateInfo) { localStorage.setItem('ota_dismissed', updateInfo.latest_version); setUpdateDismissed(true); }
  };
  if (!user) {
    return (
      <div className="min-h-screen bg-[#F0F2F5] flex items-center justify-center p-5 mobile-app-root" style={{ fontFamily: 'Roboto, sans-serif' }}>
        <div className="w-full">
          <div className="text-center mb-6">
            <div className="w-16 h-16 bg-[#0052CC] rounded-2xl mx-auto mb-3 flex items-center justify-center"><WifiHigh size={32} weight="bold" className="text-white" /></div>
            <h1 className="text-xl font-bold text-[#1A1C1E]">Myanmar Link ISP</h1>
            <p className="text-xs text-[#666] mt-0.5">Field Team Mobile App</p>
          </div>
          <div className="bg-white rounded-2xl p-5 shadow-sm">
            <h2 className="text-base font-medium text-center mb-4">Staff Login</h2>
            {loginError && <div className="bg-[#FFEBEE] text-[#B71C1C] text-xs rounded-lg p-2.5 mb-3 text-center font-medium">{loginError}</div>}
            <div className="space-y-3 mt-3">
              <div>
                <label className="text-[11px] font-medium text-[#666] mb-1 block">Staff ID</label>
                <input value={loginStaffId} onChange={e => setLoginStaffId(e.target.value)}
                  placeholder="e.g. 201"
                  className="w-full bg-[#F5F6F8] rounded-xl px-3.5 py-3 text-sm border border-[#E0E0E0] outline-none focus:border-[#0052CC]" />
              </div>
              <div>
                <label className="text-[11px] font-medium text-[#666] mb-1 block">Password</label>
                <div className="relative">
                  <input type={showLoginPw ? 'text' : 'password'} value={loginPassword} onChange={e => setLoginPassword(e.target.value)}
                    placeholder="Enter password"
                    className="w-full bg-[#F5F6F8] rounded-xl px-3.5 py-3 text-sm border border-[#E0E0E0] outline-none focus:border-[#0052CC] pr-10" />
                  <button type="button" onClick={() => setShowLoginPw(v => !v)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-[#999]">
                    {showLoginPw ? <EyeSlash size={18} /> : <Eye size={18} />}
                  </button>
                </div>
              </div>
              <div>
                <label className="text-[11px] font-medium text-[#666] mb-1 block">Department</label>
                <div className="relative">
                  <select value={loginDept} onChange={e => setLoginDept(e.target.value)}
                    className="w-full bg-[#F5F6F8] rounded-xl px-3.5 py-3 text-sm border border-[#E0E0E0] outline-none focus:border-[#0052CC] appearance-none">
                    {['IMFT', 'BMFT', 'NOC', 'Admin', 'Marketing'].map(d => <option key={d} value={d}>{d}</option>)}
                  </select>
                  <div className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none text-[#666]">
                    <CaretDown size={16} weight="bold" />
                  </div>
                </div>
              </div>
              <div>
                <label className="text-[11px] font-medium text-[#666] mb-1 block">Brand</label>
                <div className="relative">
                  <select value={loginBrand} onChange={e => setLoginBrand(e.target.value)}
                    className="w-full bg-[#F5F6F8] rounded-xl px-3.5 py-3 text-sm border border-[#E0E0E0] outline-none focus:border-[#0052CC] appearance-none">
                    {['MyanmarLink', 'CityLink', 'SkyLink'].map(b => <option key={b} value={b}>{b}</option>)}
                  </select>
                  <div className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none text-[#666]">
                    <CaretDown size={16} weight="bold" />
                  </div>
                </div>
              </div>
              <button onClick={() => handleMobileLogin()} disabled={loginLoading || !loginStaffId || !loginPassword}
                className="w-full bg-[#0052CC] text-white rounded-2xl py-3.5 font-bold text-sm disabled:opacity-50">
                {loginLoading ? 'Signing in...': 'Sign In'}
              </button>
            </div>
          </div>
          {/* PWA Install Button on Login */}
          {installPrompt && !isInstalled && (
            <button onClick={handleInstall} className="w-full mt-3 bg-[#006D3B] text-white rounded-2xl py-3.5 font-bold text-sm flex items-center justify-center gap-2 active:bg-[#004D2A]">
              <CloudArrowUp size={20} weight="fill" /> Install App on Phone
            </button>
          )}
        </div>
      </div>
    );
  }

  // ===== AUTHENTICATED APP BELOW =====

  // ===== FTTH SALES DASHBOARD (for Sales Staff role) =====
  if (isSalesRole) {
    const filteredSales = salesFilter === 'all' ? salesRecords : salesRecords.filter(s => s.status === salesFilter);
    const STATUS_COLORS = {
      'Pending': { bg: '#FFF8E1', text: '#F57F17' },
      'Installed': { bg: '#E8F5E9', text: '#1B5E20' },
      'Canceled': { bg: '#FFEBEE', text: '#B71C1C' },
      'In Progress': { bg: '#E3F2FD', text: '#0D47A1' },
    };

    return (
      <div className="min-h-screen bg-[#F0F2F5] mobile-app-root pb-20" style={{ fontFamily: 'Roboto, sans-serif' }}>
        {/* Header */}
        <div className="bg-[#1B5E20] text-white px-4 py-2.5 flex items-center justify-between sticky top-0 z-50">
          
          <span className="font-bold text-sm flex-1 text-center">
            {screen === 'home' ? 'FTTH Sales' : screen === 'sales-list' ? 'My Sales' : screen === 'new-sale' ? 'New Sale' : screen === 'sale-detail' ? 'Sale Details' : 'FTTH Sales'}
          </span>
          <div className="flex items-center gap-2">
            {online ? <WifiHigh size={16} className="text-green-300" /> : <WifiSlash size={16} className="text-red-300 animate-pulse" />}
            
          </div>
        </div>

        {/* Offline Banner */}
        {!online && (
          <div className="bg-[#FFF3E0] px-4 py-2 flex items-center gap-2 text-xs text-[#E65100]">
            <WifiSlash size={14} /><span className="font-medium">Offline Mode</span>
          </div>
        )}

        {/* HOME - Sales Dashboard */}
        {screen === 'home' && (
          <div className="p-4 space-y-3">
            {/* Welcome Card */}
            <div className="bg-gradient-to-r from-[#1B5E20] to-[#2E7D32] rounded-2xl p-4 text-white">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-lg font-bold">Welcome, {user?.name}</p>
                  <p className="text-xs opacity-80">{user?.team_name || 'FTTH Sales Team'} · {new Date().toLocaleDateString('en-US', { weekday: 'long', month: 'short', day: 'numeric' })}</p>
                </div>
                <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center">
                  <ShoppingCart size={22} weight="bold" />
                </div>
              </div>
            </div>

            {/* Stats Cards */}
            <div className="grid grid-cols-4 gap-2">
              {[
                { l: 'Total', v: salesStats.total || 0, c: '#1B5E20' },
                { l: 'Pending', v: salesStats.pending || 0, c: '#F57F17' },
                { l: 'Installed', v: salesStats.completed || 0, c: '#006D3B' },
                { l: 'Canceled', v: salesStats.canceled || 0, c: '#BA1A1A' }
              ].map((k, i) => (
                <div key={i} className="bg-white rounded-xl p-3 text-center shadow-sm">
                  <p className="text-xl font-bold tabular-nums" style={{ color: k.c }}>{k.v}</p>
                  <p className="text-[9px] uppercase text-[#999]">{k.l}</p>
                </div>
              ))}
            </div>

            {/* Revenue Card */}
            <div className="bg-white rounded-xl p-4 shadow-sm">
              <div className="flex items-center gap-2 mb-2">
                <CurrencyDollar size={18} className="text-[#1B5E20]" />
                <span className="text-sm font-bold">Revenue</span>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="bg-[#F5F6F8] rounded-lg p-3 text-center">
                  <p className="text-lg font-bold text-[#1B5E20] tabular-nums">{(salesStats.revenue_mmk || 0).toLocaleString()}</p>
                  <p className="text-[10px] text-[#999]">MMK</p>
                </div>
                <div className="bg-[#F5F6F8] rounded-lg p-3 text-center">
                  <p className="text-lg font-bold text-[#0052CC] tabular-nums">${(salesStats.revenue_usd || 0).toLocaleString()}</p>
                  <p className="text-[10px] text-[#999]">USD</p>
                </div>
              </div>
            </div>

            {/* Quick Actions */}
            <div className="grid grid-cols-3 gap-2">
              <button onClick={() => { setScreen('new-sale'); setShowNewSaleForm(true); }} className="bg-[#1B5E20] text-white rounded-xl p-4 text-center shadow-sm active:bg-[#145218]">
                <Plus size={24} className="mx-auto mb-1" weight="bold" />
                <span className="text-xs font-bold">New Sale</span>
              </button>
              <button onClick={() => { setScreen('sales-list'); loadSalesData(); }} className="bg-white rounded-xl p-4 text-center shadow-sm active:bg-[#F5F5F5]">
                <Clock size={24} className="mx-auto text-[#1B5E20] mb-1" />
                <span className="text-xs font-medium">My Sales</span>
              </button>
              <button onClick={loadSalesData} disabled={salesLoading} className="bg-white rounded-xl p-4 text-center shadow-sm active:bg-[#F5F5F5]">
                <ArrowClockwise size={24} className={`mx-auto text-[#7C4DFF] mb-1 ${salesLoading ? 'animate-spin' : ''}`} />
                <span className="text-xs font-medium">Refresh</span>
              </button>
            </div>

            {/* Recent Sales */}
            <div className="flex items-center justify-between pt-1">
              <p className="text-sm font-medium">Recent Sales</p>
              <button onClick={() => setScreen('sales-list')} className="text-xs text-[#1B5E20] font-bold">View All</button>
            </div>
            {salesRecords.slice(0, 5).map(s => (
              <button key={s.sale_id} onClick={() => { setSelectedSale(s); setScreen('sale-detail'); }}
                className="w-full bg-white rounded-xl p-4 text-left shadow-sm active:bg-[#F5F5F5]">
                <div className="flex items-center justify-between mb-1">
                  <span className="text-xs font-bold text-[#1B5E20]">{s.sale_id}</span>
                  <span className="text-[10px] font-bold px-2 py-0.5 rounded-full" style={{ color: (STATUS_COLORS[s.status] || STATUS_COLORS['Pending']).text, background: (STATUS_COLORS[s.status] || STATUS_COLORS['Pending']).bg }}>{s.status}</span>
                </div>
                <p className="text-sm font-medium">{s.customer_name}</p>
                <div className="flex items-center justify-between mt-1">
                  <p className="text-xs text-[#999]">{s.township} · {s.plan_name || 'FTTH'}</p>
                  <p className="text-xs font-bold text-[#1B5E20]">{s.price ? `${s.price.toLocaleString()} ${s.currency || 'MMK'}` : ''}</p>
                </div>
              </button>
            ))}
            {salesRecords.length === 0 && (
              <div className="bg-white rounded-xl p-8 text-center">
                <ShoppingCart size={32} className="mx-auto text-[#999] mb-2" />
                <p className="text-sm text-[#999]">{online ? 'No sales yet — create your first sale!' : 'Connect to load sales data'}</p>
              </div>
            )}

            {/* PWA Install Banner */}
            {installPrompt && !isInstalled && (
              <button onClick={handleInstall} className="w-full bg-[#006D3B] text-white rounded-xl p-3 flex items-center gap-3 active:bg-[#004D2A] shadow-sm">
                <div className="w-10 h-10 bg-white/20 rounded-lg flex items-center justify-center flex-shrink-0"><CloudArrowUp size={22} weight="fill" /></div>
                <div className="text-left flex-1"><p className="text-sm font-bold">Install App</p><p className="text-[10px] opacity-80">Add to home screen for quick access</p></div>
              </button>
            )}
          </div>
        )}

        {/* NEW SALE FORM */}
        {screen === 'new-sale' && (
          <div className="p-4 space-y-3">
            <div className="bg-white rounded-2xl p-5 shadow-sm space-y-3">
              <h3 className="text-base font-bold text-center mb-2">New FTTH Sale</h3>

              <div>
                <label className="text-[11px] font-medium text-[#666] mb-1 block">Customer Name *</label>
                <input value={newSale.customer_name} onChange={e => setNewSale({...newSale, customer_name: e.target.value})} placeholder="Enter customer name" className="w-full bg-[#F5F6F8] rounded-xl px-3.5 py-3 text-sm border border-[#E0E0E0] outline-none focus:border-[#1B5E20]" />
              </div>

              <div>
                <label className="text-[11px] font-medium text-[#666] mb-1 block">Phone Number *</label>
                <input value={newSale.phone} onChange={e => setNewSale({...newSale, phone: e.target.value})} placeholder="09xxxxxxxxx" type="tel" className="w-full bg-[#F5F6F8] rounded-xl px-3.5 py-3 text-sm border border-[#E0E0E0] outline-none focus:border-[#1B5E20]" />
              </div>

              <div>
                <label className="text-[11px] font-medium text-[#666] mb-1 block">Township *</label>
                <input value={newSale.township} onChange={e => setNewSale({...newSale, township: e.target.value})} placeholder="e.g. Hlaing, Insein, Tamwe" className="w-full bg-[#F5F6F8] rounded-xl px-3.5 py-3 text-sm border border-[#E0E0E0] outline-none focus:border-[#1B5E20]" />
              </div>

              <div>
                <label className="text-[11px] font-medium text-[#666] mb-1 block">Address</label>
                <input value={newSale.address} onChange={e => setNewSale({...newSale, address: e.target.value})} placeholder="Full address" className="w-full bg-[#F5F6F8] rounded-xl px-3.5 py-3 text-sm border border-[#E0E0E0] outline-none focus:border-[#1B5E20]" />
              </div>

              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="text-[11px] font-medium text-[#666] mb-1 block">Plan Name</label>
                  <input value={newSale.plan_name} onChange={e => setNewSale({...newSale, plan_name: e.target.value})} placeholder="e.g. Home Basic" className="w-full bg-[#F5F6F8] rounded-xl px-3.5 py-3 text-sm border border-[#E0E0E0] outline-none focus:border-[#1B5E20]" />
                </div>
                <div>
                  <label className="text-[11px] font-medium text-[#666] mb-1 block">Speed</label>
                  <input value={newSale.plan_speed} onChange={e => setNewSale({...newSale, plan_speed: e.target.value})} placeholder="e.g. 30 Mbps" className="w-full bg-[#F5F6F8] rounded-xl px-3.5 py-3 text-sm border border-[#E0E0E0] outline-none focus:border-[#1B5E20]" />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="text-[11px] font-medium text-[#666] mb-1 block">Price</label>
                  <input value={newSale.price} onChange={e => setNewSale({...newSale, price: e.target.value})} placeholder="0" type="number" className="w-full bg-[#F5F6F8] rounded-xl px-3.5 py-3 text-sm border border-[#E0E0E0] outline-none focus:border-[#1B5E20]" />
                </div>
                <div>
                  <label className="text-[11px] font-medium text-[#666] mb-1 block">Currency</label>
                  <select value={newSale.currency} onChange={e => setNewSale({...newSale, currency: e.target.value})} className="w-full bg-[#F5F6F8] rounded-xl px-3.5 py-3 text-sm border border-[#E0E0E0] outline-none focus:border-[#1B5E20]">
                    <option value="MMK">MMK</option>
                    <option value="USD">USD</option>
                  </select>
                </div>
              </div>

              <div>
                <label className="text-[11px] font-medium text-[#666] mb-1 block">Notes</label>
                <textarea value={newSale.notes} onChange={e => setNewSale({...newSale, notes: e.target.value})} placeholder="Additional notes..." className="w-full bg-[#F5F6F8] rounded-xl px-3.5 py-3 text-sm border border-[#E0E0E0] outline-none focus:border-[#1B5E20] min-h-[60px]" />
              </div>

              <button onClick={submitNewSale} disabled={salesLoading} className="w-full bg-[#1B5E20] text-white rounded-2xl py-3.5 font-bold text-sm disabled:opacity-50 active:bg-[#145218]">
                {salesLoading ? 'Creating...' : 'Create Sale'}
              </button>
            </div>
          </div>
        )}

        {/* SALES LIST */}
        {screen === 'sales-list' && (
          <div className="p-4 space-y-3">
            {/* Filter Tabs */}
            <div className="flex gap-1.5 overflow-x-auto pb-1">
              {[{ v: 'all', l: 'All' }, { v: 'Pending', l: 'Pending' }, { v: 'Installed', l: 'Installed' }, { v: 'Canceled', l: 'Canceled' }].map(f => (
                <button key={f.v} onClick={() => setSalesFilter(f.v)} className={`px-3 py-1.5 rounded-full text-xs font-bold whitespace-nowrap ${salesFilter === f.v ? 'bg-[#1B5E20] text-white' : 'bg-white text-[#666]'}`}>{f.l}</button>
              ))}
            </div>

            {filteredSales.map(s => (
              <button key={s.sale_id} onClick={() => { setSelectedSale(s); setScreen('sale-detail'); }}
                className="w-full bg-white rounded-xl p-4 text-left shadow-sm active:bg-[#F5F5F5]">
                <div className="flex items-center justify-between mb-1">
                  <span className="text-xs font-bold text-[#1B5E20]">{s.sale_id}</span>
                  <span className="text-[10px] font-bold px-2 py-0.5 rounded-full" style={{ color: (STATUS_COLORS[s.status] || STATUS_COLORS['Pending']).text, background: (STATUS_COLORS[s.status] || STATUS_COLORS['Pending']).bg }}>{s.status}</span>
                </div>
                <p className="text-sm font-medium">{s.customer_name}</p>
                <div className="flex items-center justify-between mt-1">
                  <p className="text-xs text-[#999] flex items-center gap-1"><MapPin size={12} />{s.township}</p>
                  <p className="text-xs font-bold text-[#1B5E20]">{s.price ? `${s.price.toLocaleString()} ${s.currency || 'MMK'}` : ''}</p>
                </div>
                <p className="text-[10px] text-[#999] mt-1">{s.created_at ? new Date(s.created_at).toLocaleDateString() : ''}</p>
              </button>
            ))}
            {filteredSales.length === 0 && (
              <div className="bg-white rounded-xl p-8 text-center text-sm text-[#999]">No sales found</div>
            )}
          </div>
        )}

        {/* SALE DETAIL */}
        {screen === 'sale-detail' && selectedSale && (
          <div className="p-4 space-y-3">
            <div className="bg-white rounded-xl p-4 shadow-sm">
              <div className="flex items-center justify-between mb-3">
                <span className="text-xs font-bold text-[#1B5E20]">{selectedSale.sale_id}</span>
                <span className="text-[10px] font-bold px-2 py-0.5 rounded-full" style={{ color: (STATUS_COLORS[selectedSale.status] || STATUS_COLORS['Pending']).text, background: (STATUS_COLORS[selectedSale.status] || STATUS_COLORS['Pending']).bg }}>{selectedSale.status}</span>
              </div>

              <div className="space-y-3">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-[#E8F5E9] rounded-full flex items-center justify-center"><User size={20} className="text-[#1B5E20]" /></div>
                  <div>
                    <p className="text-base font-bold">{selectedSale.customer_name}</p>
                    <p className="text-xs text-[#666]">{selectedSale.phone}</p>
                  </div>
                </div>

                <div className="border-t border-[#F0F0F0] pt-3 space-y-2">
                  <div className="flex items-center gap-2 text-sm">
                    <MapPin size={16} className="text-[#999]" />
                    <span>{selectedSale.township} {selectedSale.address ? `· ${selectedSale.address}` : ''}</span>
                  </div>
                  {selectedSale.plan_name && (
                    <div className="flex items-center gap-2 text-sm">
                      <WifiHigh size={16} className="text-[#999]" />
                      <span>{selectedSale.plan_name} {selectedSale.plan_speed ? `(${selectedSale.plan_speed})` : ''}</span>
                    </div>
                  )}
                  {selectedSale.price > 0 && (
                    <div className="flex items-center gap-2 text-sm">
                      <CurrencyDollar size={16} className="text-[#999]" />
                      <span className="font-bold">{selectedSale.price.toLocaleString()} {selectedSale.currency || 'MMK'}</span>
                    </div>
                  )}
                  {selectedSale.created_at && (
                    <div className="flex items-center gap-2 text-sm">
                      <CalendarBlank size={16} className="text-[#999]" />
                      <span>{new Date(selectedSale.created_at).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' })}</span>
                    </div>
                  )}
                </div>

                {selectedSale.notes && (
                  <div className="bg-[#F5F6F8] rounded-lg p-3 mt-2">
                    <p className="text-[11px] font-medium text-[#666] mb-1">Notes</p>
                    <p className="text-sm">{selectedSale.notes}</p>
                  </div>
                )}
              </div>
            </div>

            {/* Action Buttons */}
            <div className="grid grid-cols-2 gap-2">
              <a href={`tel:${selectedSale.phone}`} className="bg-[#E3F2FD] rounded-xl p-3 text-center active:bg-[#BBDEFB]">
                <Phone size={20} className="mx-auto text-[#0052CC]" />
                <p className="text-[10px] font-medium text-[#0052CC] mt-1">Call Customer</p>
              </a>
              <a href={`https://www.google.com/maps/search/${encodeURIComponent(selectedSale.township + ' ' + (selectedSale.address || ''))}`} target="_blank" rel="noreferrer" className="bg-[#E8F5E9] rounded-xl p-3 text-center active:bg-[#C8E6C9]">
                <MapPin size={20} className="mx-auto text-[#006D3B]" />
                <p className="text-[10px] font-medium text-[#006D3B] mt-1">Navigate</p>
              </a>
            </div>
          </div>
        )}

        {/* Bottom Nav - Sales */}
        <div className="fixed bottom-0 left-0 right-0 mobile-app-root bg-white border-t border-[#E0E0E0] flex items-center justify-around py-2 z-50">
          {[
            { icon: '🏠', label: 'Home', s: 'home' },
            { icon: '➕', label: 'New Sale', s: 'new-sale' },
            { icon: '📋', label: 'My Sales', s: 'sales-list' }
          ].map(n => (
            <button key={n.s} onClick={() => { setScreen(n.s); setSelectedSale(null); }} className={`flex flex-col items-center gap-0.5 px-4 py-1 ${screen === n.s ? 'text-[#1B5E20]' : 'text-[#999]'}`}>
              <span className="text-lg">{n.icon}</span>
              <span className="text-[10px] font-medium">{n.label}</span>
            </button>
          ))}
        </div>
      </div>
    );
  }

  // ===== TECHNICIAN APP (IMFT/BMFT/NOC) BELOW =====

  // Update task — works offline
  const updateTask = async (tid, data) => {
    setLoading(true);
    // 1. Update local immediately
    const updated = tasks.map(t => t.task_id === tid ? { ...t, ...data, _localUpdate: true } : t);
    setTasks(updated);
    const updatedTask = updated.find(t => t.task_id === tid);
    if (updatedTask) await updateLocalTask(updatedTask);

    // 2. Queue for server sync
    addToSyncQueue({ type: 'task_update', task_id: tid, data, queued_at: new Date().toISOString() });
    await updateSyncStatus();

    // 3. Try immediate sync if online
    if (navigator.onLine) {
      try {
        const _dept2 = user && user.department ? user.department : 'IMFT';
        const _taskUrl = _dept2 === 'BMFT' ? `${API}/bmft/tasks/${tid}` : `${API}/imft/tasks/${tid}`;
        const res = await fetch(_taskUrl, { method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(data) });
        if (res.ok) {
          toast.success('Updated & synced');
          await loadFromServer();
        } else {
          toast('Saved locally — will sync later', { icon: '📴' });
        }
      } catch {
        toast('Saved offline — will sync when online', { icon: '📴' });
      }
    } else {
      toast('Saved offline', { icon: '💾' });
    }
    setSelectedTask(null); setScreen('home');
    setLoading(false);
  };

  // GPS Proof-of-Work Check-In

  const getGPS = () => new Promise((resolve, reject) => {
    if (!navigator.geolocation) { reject('GPS not available'); return; }
    navigator.geolocation.getCurrentPosition(
      (p) => resolve({ latitude: p.coords.latitude, longitude: p.coords.longitude, accuracy: p.coords.accuracy }),
      (err) => reject(err.message || 'GPS failed'),
      { enableHighAccuracy: true, timeout: 15000 }
    );
  });

  const gpsCheckIn = async (task) => {
    setGettingGPS(true); setGpsStatus(null);
    try {
      const pos = await getGPS();
      if (navigator.onLine) {
        const res = await fetch(`${API}/imft/tasks/${task.task_id}/gps-checkin`, {
          method: 'POST', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ latitude: pos.latitude, longitude: pos.longitude })
        });
        const data = await res.json();
        setGpsStatus(data);
        if (data.warning) toast(data.warning, { icon: '⚠️', duration: 5000 });
        else toast.success(`Checked in! ${data.distance_m !== null ? `${data.distance_m}m from customer` : 'GPS verified'}`);
        await loadFromServer();
        setSelectedTask({ ...task, status: 'In Progress', gps_checkin: data.gps_proof });
      } else {
        // Offline check-in
        await updateTask(task.task_id, { status: 'In Progress', check_in_time: new Date().toISOString(), check_in_gps: `${pos.latitude},${pos.longitude}`, gps_checkin: { tech_lat: pos.latitude, tech_lng: pos.longitude, timestamp: new Date().toISOString() } });
      }
    } catch (err) {
      toast.error(`GPS Error: ${err}`);
      // Allow check-in without GPS as fallback
      if (window.confirm('GPS failed. Check in without GPS verification?')) {
        await updateTask(task.task_id, { status: 'In Progress', check_in_time: new Date().toISOString(), check_in_gps: 'GPS unavailable' });
      }
    }
    finally { setGettingGPS(false); }
  };

  // Photo capture
  const capturePhoto = () => {
    const input = document.createElement('input');
    input.type = 'file'; input.accept = 'image/*'; input.capture = 'environment';
    input.onchange = (e) => {
      const file = e.target.files?.[0];
      if (!file) return;
      const reader = new FileReader();
      reader.onload = () => {
        setPhotos(prev => [...prev, { data: reader.result, label: `Photo ${prev.length + 1}`, time: new Date().toISOString() }]);
        toast.success('Photo captured');
      };
      reader.readAsDataURL(file);
    };
    input.click();
  };

  // BMFT: Start job (set In Progress)
  const bmftStartJob = async (task) => {
    setLoading(true);
    try {
      const token = localStorage.getItem('session_token');
      if (navigator.onLine) {
        const res = await fetch(`${API}/bmft/tasks/${task.task_id}`, {
          method: 'PUT', headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
          body: JSON.stringify({ status: 'In Progress', check_in_time: new Date().toISOString() })
        });
        if (res.ok) {
          const updated = await res.json();
          setSelectedTask(updated);
          setTasks(prev => prev.map(t => t.task_id === task.task_id ? updated : t));
          toast.success('Job started!');
        } else { throw new Error('Failed to start job'); }
      } else {
        const updated = { ...task, status: 'In Progress', check_in_time: new Date().toISOString(), _localUpdate: true };
        setSelectedTask(updated);
        setTasks(prev => prev.map(t => t.task_id === task.task_id ? updated : t));
        await updateLocalTask(updated);
        addToSyncQueue({ type: 'task_update', task_id: task.task_id, data: { status: 'In Progress', check_in_time: new Date().toISOString() }, queued_at: new Date().toISOString() });
        toast('Started offline — will sync', { icon: '📴' });
      }
    } catch (err) { toast.error(err.message); }
    finally { setLoading(false); }
  };

  // BMFT: Add onsite progress update
  const bmftAddOnsiteUpdate = async () => {
    if (!bmftOnsiteNote.trim()) { toast.error('Add update note'); return; }
    setBmftSubmitting(true);
    try {
      const token = localStorage.getItem('session_token');
      const payload = { description: bmftOnsiteNote, progress: bmftProgress, before_photos: photos.map(p => ({ label: p.label, data: p.data, time: p.time })), update_type: 'progress' };
      if (navigator.onLine) {
        const res = await fetch(`${API}/bmft/tasks/${selectedTask.task_id}/onsite-update`, {
          method: 'POST', headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
          body: JSON.stringify(payload)
        });
        if (res.ok) {
          const updated = await res.json();
          setSelectedTask(updated);
          setTasks(prev => prev.map(t => t.task_id === selectedTask.task_id ? updated : t));
          toast.success('Progress updated!');
          setBmftOnsiteNote(''); setPhotos([]);
        } else {
          const d = await res.json();
          throw new Error(d.detail || 'Failed to update');
        }
      } else {
        const updated = { ...selectedTask, progress: bmftProgress, _localUpdate: true, onsite_updates: [...(selectedTask.onsite_updates || []), { ...payload, updated_at: new Date().toISOString() }] };
        setSelectedTask(updated);
        setTasks(prev => prev.map(t => t.task_id === selectedTask.task_id ? updated : t));
        await updateLocalTask(updated);
        toast('Saved offline', { icon: '💾' });
        setBmftOnsiteNote(''); setPhotos([]);
      }
    } catch (err) { toast.error(err.message); }
    finally { setBmftSubmitting(false); }
  };

  // BMFT: Submit completion report
  const bmftSubmitReport = async () => {
    if (!bmftReport.work_notes.trim()) { toast.error('Add work notes'); return; }
    setBmftSubmitting(true);
    try {
      const token = localStorage.getItem('session_token');
      const pos = await getGPS().catch(() => ({ latitude: 0, longitude: 0 }));
      const payload = {
        status: 'Pending Approval',
        progress: 100,
        completion_report: {
          ...bmftReport,
          photos: photos.map(p => ({ label: p.label, time: p.time })),
          completed_at: new Date().toISOString(),
          gps: { latitude: pos.latitude, longitude: pos.longitude }
        }
      };
      if (navigator.onLine) {
        const res = await fetch(`${API}/bmft/tasks/${selectedTask.task_id}`, {
          method: 'PUT', headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
          body: JSON.stringify(payload)
        });
        if (res.ok) {
          toast.success('Report submitted! Awaiting supervisor approval.');
          await loadFromServer();
          setSelectedTask(null); setScreen('home');
          setPhotos([]); setBmftReport({ work_notes: '', error_type: '', fat_name: '', fat_location: '', fiber_meter_used: '', onu_sn: '', remark: '' });
        } else { const d = await res.json(); throw new Error(d.detail || 'Submit failed'); }
      } else {
        const updated = { ...selectedTask, ...payload, _localUpdate: true };
        setSelectedTask(updated);
        setTasks(prev => prev.map(t => t.task_id === selectedTask.task_id ? updated : t));
        await updateLocalTask(updated);
        addToSyncQueue({ type: 'task_update', task_id: selectedTask.task_id, data: payload, queued_at: new Date().toISOString() });
        toast('Report saved offline — will sync', { icon: '📴' });
        setSelectedTask(null); setScreen('home');
        setPhotos([]); setBmftReport({ work_notes: '', error_type: '', fat_name: '', fat_location: '', fiber_meter_used: '', onu_sn: '', remark: '' });
      }
    } catch (err) { toast.error(err.message); }
    finally { setBmftSubmitting(false); }
  };

  const mapSavePoint = async () => {
    if (!mapFormPos) return;
    const token = localStorage.getItem('session_token');
    const typeLabel = mapAddMode === 'odb' ? 'ODB' : mapAddMode === 'user_home' ? 'User Home' : 'Cable Route';
    const body = {
      obj_type: mapAddMode === 'user_home' ? 'customer' : mapAddMode,
      name: mapForm.name || `${typeLabel}-${Date.now().toString(36).toUpperCase()}`,
      description: mapForm.description,
      lat: mapFormPos.lat,
      lng: mapFormPos.lng,
      ...(mapAddMode === 'odb' ? { capacity: Number(mapForm.capacity) || 16, used_ports: 0 } : {}),
      added_by_mobile: true,
      added_from_task: selectedTask?.task_id || null,
    };
    try {
      const res = await fetch(`${API}/fiber-map/objects`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify(body)
      });
      if (res.ok) {
        const created = await res.json();
        setMapObjects(prev => [...prev, created]);
        toast.success(`${typeLabel} saved to central map!`);
        setMapFormVisible(false); setMapFormPos(null);
        setMapForm({ name: '', description: '', capacity: 16 });
        setMapAddMode(null);
      } else { throw new Error('Save failed'); }
    } catch (err) { toast.error(err.message); }
  };

  const mapSaveCableRoute = async () => {
    if (mapDrawPoints.length < 2) { toast.error('Draw at least 2 points for a cable route'); return; }
    const token = localStorage.getItem('session_token');
    const body = {
      obj_type: 'cable_route',
      name: mapForm.name || `Cable-${Date.now().toString(36).toUpperCase()}`,
      description: mapForm.description,
      coordinates: mapDrawPoints,
      lat: mapDrawPoints[0].lat,
      lng: mapDrawPoints[0].lng,
      added_by_mobile: true,
      added_from_task: selectedTask?.task_id || null,
    };
    try {
      const res = await fetch(`${API}/fiber-map/objects`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify(body)
      });
      if (res.ok) {
        const created = await res.json();
        setMapObjects(prev => [...prev, created]);
        toast.success('Cable route saved to central map!');
        setMapDrawPoints([]); setMapFormVisible(false);
        setMapForm({ name: '', description: '', capacity: 16 });
        setMapAddMode(null);
      } else { throw new Error('Save failed'); }
    } catch (err) { toast.error(err.message); }
  };

  const mapDeleteObject = async (oid) => {
    if (!window.confirm('Delete this marker?')) return;
    const token = localStorage.getItem('session_token');
    try {
      await fetch(`${API}/fiber-map/objects/${oid}`, { method: 'DELETE', headers: { 'Authorization': `Bearer ${token}` } });
      setMapObjects(prev => prev.filter(o => o.object_id !== oid));
      setMapSelectedObj(null);
      toast.success('Deleted from central map');
    } catch (err) { toast.error('Delete failed'); }
  };

  // GPS completion with full proof
  const submitWithGPS = async () => {
    if (!reportForm.work_notes.trim()) { toast.error('Add work notes'); return; }
    setLoading(true);
    try {
      const pos = await getGPS().catch(() => ({ latitude: 0, longitude: 0 }));
      const pwr = reportForm.power_reading ? { receive_power: reportForm.power_reading, in_range: parseFloat(reportForm.power_reading) >= -25 && parseFloat(reportForm.power_reading) <= -18, signal_status: (parseFloat(reportForm.power_reading) >= -25 && parseFloat(reportForm.power_reading) <= -18) ? 'Good' : 'Out of Range' } : {};
      const payload = {
        latitude: pos.latitude, longitude: pos.longitude,
        photos: photos.map(p => ({ label: p.label, time: p.time })),
        signal_data: signalData,
        customer_verify: { phone: reportForm.cust_phone, house_no: reportForm.house_no, street: reportForm.street },
        work_notes: reportForm.work_notes,
        power_test: pwr,
        materials_used: [],
      };
      if (navigator.onLine) {
        const res = await fetch(`${API}/imft/tasks/${selectedTask.task_id}/gps-complete`, {
          method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(payload)
        });
        const data = await res.json();
        if (!res.ok) throw new Error(data.detail || 'Failed');
        toast.success(`Submitted! GPS: ${data.gps_proof?.distance_m !== null ? `${data.gps_proof.distance_m}m` : 'captured'}`);
        await loadFromServer();
      } else {
        await updateTask(selectedTask.task_id, { status: 'Pending Approval', ...payload });
      }
      setSelectedTask(null); setScreen('home'); setPhotos([]); setSignalData({ olt_port: '', onu_serial: '', rx_level: '', tx_level: '' });
      setReportForm({ work_notes: '', power_reading: '', cust_phone: '', house_no: '', street: '' });
    } catch (err) { toast.error(err.message || 'Submit failed'); }
    finally { setLoading(false); }
  };

  // Manual sync
  const manualSync = async () => {
    if (!navigator.onLine) { toast.error('No internet connection'); return; }
    setSyncing(true);
    try {
      const result = await processSync((p) => toast(`Syncing ${p.current}/${p.total}...`, { duration: 1000 }));
      if (result.synced > 0) toast.success(`Synced ${result.synced} items`);
      else if (result.total === 0) toast('Everything is up to date');
      if (result.failed > 0) { toast.error(`${result.failed} items failed`); setSyncStatus('error'); }
      await loadFromServer();
      await updateSyncStatus();
    } catch { toast.error('Sync failed'); setSyncStatus('error'); }
    finally { setSyncing(false); }
  };

  const today = new Date().toISOString().split('T')[0];
  const todayTasks = tasks.filter(t => !['Completed', 'Cancelled'].includes(t.status));
  const SYNC_COLORS = { synced: '#006D3B', pending: '#F57F17', error: '#BA1A1A' };

  return (
    <div className="min-h-screen bg-[#F0F2F5] mobile-app-root pb-16" style={{ fontFamily: 'Roboto, sans-serif' }}>
      {/* Header with Sync Status */}
      <div className="bg-[#0052CC] text-white px-4 py-2.5 flex items-center justify-between sticky top-0 z-50">
        
        <span className="font-bold text-sm flex-1 text-center">{screen === 'home' ? 'Myanmar Link Field' : screen === 'jobs' ? 'My Jobs' : screen === 'sync' ? 'Sync Status' : screen === 'map' ? 'Infrastructure Map' : screen === 'odb-check' ? 'ODB Check' : screen === 'team-movement' ? 'Team Movement' : 'Job Details'}</span>
        {/* Online/Offline + Sync indicator */}
        <div className="flex items-center gap-2">
          {online ? <WifiHigh size={16} className="text-green-300" /> : <WifiSlash size={16} className="text-red-300 animate-pulse" />}
          {user?.department === 'BMFT' && (
            <div className="flex items-center gap-0.5" title={trackingActive ? 'GPS Tracking Active' : 'Tracking Off'}>
              <div className={`w-2 h-2 rounded-full ${trackingActive ? 'bg-green-300 animate-pulse' : 'bg-gray-400'}`} />
              <span className="text-[9px] text-white/70">{trackingActive ? 'GPS' : ''}</span>
            </div>
          )}
          <div className="w-2 h-2 rounded-full" style={{ background: SYNC_COLORS[syncStatus] }} />
        </div>
      </div>

      {/* Offline Banner */}
      {!online && (
        <div className="bg-[#FFF3E0] px-4 py-2 flex items-center gap-2 text-xs text-[#E65100]">
          <WifiSlash size={14} /><span className="font-medium">Offline Mode</span><span>— Data saved locally, will sync when online</span>
        </div>
      )}

      {/* Pending Sync Banner */}
      {pendingCount > 0 && online && (
        <div className="bg-[#FFF8E1] px-4 py-2 flex items-center justify-between text-xs">
          <span className="flex items-center gap-1.5 text-[#F57F17]"><CloudArrowUp size={14} /><span className="font-medium">{pendingCount} pending sync</span></span>
          <button onClick={manualSync} disabled={syncing} className="text-[#0052CC] font-bold flex items-center gap-1"><ArrowClockwise size={12} className={syncing ? 'animate-spin' : ''} />{syncing ? 'Syncing...' : 'Sync Now'}</button>
        </div>
      )}

      {/* OTA Update Banner */}
      {updateInfo && updateInfo.update_available && !updateDismissed && (
        <div className={`px-4 py-3 flex items-center justify-between ${updateInfo.mandatory ? 'bg-[#FFEBEE]' : 'bg-[#E3F2FD]'}`}>
          <div className="flex items-center gap-2 flex-1">
            <CloudArrowUp size={18} className={updateInfo.mandatory ? 'text-[#BA1A1A]' : 'text-[#0052CC]'} />
            <div>
              <p className="text-xs font-bold" style={{ color: updateInfo.mandatory ? '#BA1A1A' : '#0052CC' }}>
                {updateInfo.mandatory ? 'Mandatory Update Required' : 'New Update Available'} — v{updateInfo.latest_version}
              </p>
              {updateInfo.release_notes && <p className="text-[10px] text-[#666] line-clamp-1">{updateInfo.release_notes}</p>}
            </div>
          </div>
          <div className="flex gap-2 flex-shrink-0">
            {updateInfo.download_url && <a href={updateInfo.download_url} target="_blank" rel="noreferrer" className="text-[10px] bg-[#0052CC] text-white px-3 py-1.5 rounded-full font-bold">Update Now</a>}
            {!updateInfo.mandatory && <button onClick={dismissOTAUpdate} className="text-[10px] text-[#666] font-medium">Later</button>}
          </div>
        </div>
      )}

      {/* Mandatory Update Blocker */}
      {updateInfo && updateInfo.mandatory && !updateDismissed && (
        <div className="fixed inset-0 bg-black/50 z-[100] flex items-center justify-center p-6">
          <div className="bg-white rounded-2xl p-6 max-w-sm w-full text-center">
            <Warning size={48} className="mx-auto text-[#BA1A1A] mb-3" weight="fill" />
            <h3 className="text-lg font-bold mb-1">Critical Update Required</h3>
            <p className="text-sm text-[#666] mb-2">Version {updateInfo.latest_version} is required to continue.</p>
            {updateInfo.release_notes && <p className="text-xs bg-[#F5F5F5] rounded-lg p-3 text-left mb-3">{updateInfo.release_notes}</p>}
            {updateInfo.download_url ? (
              <a href={updateInfo.download_url} target="_blank" rel="noreferrer" className="block w-full bg-[#BA1A1A] text-white rounded-2xl py-3 font-bold">Download Update</a>
            ) : (
              <p className="text-xs text-[#999]">Update will be available shortly.</p>
            )}
          </div>
        </div>
      )}

      {/* HOME */}
      {screen === 'home' && (
        <div className="p-4 space-y-3">
          <div className="bg-[#0052CC] rounded-2xl p-4 text-white">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-lg font-bold">Welcome, {user?.name}</p>
                <p className="text-xs opacity-70">{user?.department} · {new Date().toLocaleDateString('en-US', { weekday: 'long', month: 'short', day: 'numeric' })}</p>
              </div>
              {!online && <div className="bg-white/20 rounded-lg px-2 py-1 text-[10px] font-bold">OFFLINE</div>}
            </div>
          </div>

          <div className="grid grid-cols-4 gap-2">
            {[{ l: 'Today', v: stats.today_total || todayTasks.length, c: '#0052CC' }, { l: 'Done', v: stats.today_completed || todayTasks.filter(t => t.status === 'Completed').length, c: '#006D3B' }, { l: 'Pending', v: stats.today_pending || todayTasks.filter(t => !['Completed', 'Cancelled'].includes(t.status)).length, c: '#F57F17' }, { l: 'Sync', v: pendingCount, c: SYNC_COLORS[syncStatus] }].map((k, i) => (
              <div key={i} onClick={i === 3 ? () => setScreen('sync') : undefined} className="bg-white rounded-xl p-3 text-center shadow-sm active:bg-[#F5F5F5]">
                <p className="text-xl font-bold tabular-nums" style={{ color: k.c }}>{k.v}</p>
                <p className="text-[9px] uppercase text-[#999]">{k.l}</p>
              </div>
            ))}
          </div>

          {/* Daily Target - IMFT only; BMFT shows full dashboard */}
          {user && user.department === 'BMFT' ? (
            <>
              {/* Check-in Status Card */}
              <div className="bg-white rounded-xl p-4 shadow-sm">
                <div className="flex items-center justify-between mb-3">
                  <span className="text-sm font-bold">Today's Schedule</span>
                  <span className="text-[10px] text-[#999]">{new Date().toLocaleDateString('en-US', { weekday: 'long', month: 'short', day: 'numeric' })}</span>
                </div>
                <div className="grid grid-cols-2 gap-2 mb-3">
                  <div className="bg-[#E3F2FD] rounded-xl p-3 text-center">
                    <p className="text-xs text-[#0052CC] font-medium mb-0.5">Check-In</p>
                    <p className="text-base font-bold text-[#0052CC]">{bmftSchedule.checkin_time ? new Date(bmftSchedule.checkin_time).toLocaleTimeString([], {hour:'2-digit',minute:'2-digit'}) : '--:--'}</p>
                  </div>
                  <div className="bg-[#E8F5E9] rounded-xl p-3 text-center">
                    <p className="text-xs text-[#006D3B] font-medium mb-0.5">Check-Out</p>
                    <p className="text-base font-bold text-[#006D3B]">{bmftSchedule.checkout_time ? new Date(bmftSchedule.checkout_time).toLocaleTimeString([], {hour:'2-digit',minute:'2-digit'}) : '--:--'}</p>
                  </div>
                </div>
                {bmftSchedule.next_job && (
                  <div className="bg-[#FFF8E1] rounded-xl p-3">
                    <p className="text-[10px] text-[#F57F17] font-bold mb-0.5">NEXT JOB</p>
                    <p className="text-sm font-medium">{bmftSchedule.next_job.task_type} — {bmftSchedule.next_job.location || bmftSchedule.next_job.township}</p>
                    <p className="text-[10px] text-[#999]">{bmftSchedule.next_job.task_id} · {bmftSchedule.next_job.priority}</p>
                  </div>
                )}
              </div>

              {/* KPI + Bonus Card */}
              <div className="bg-white rounded-xl p-4 shadow-sm">
                <div className="flex items-center justify-between mb-3">
                  <span className="text-sm font-bold">Today's KPI &amp; Bonus</span>
                  <span className="text-xs bg-[#E8F5E9] text-[#006D3B] px-2 py-0.5 rounded-full font-bold">{bmftKpi.kpi_pct || 0}%</span>
                </div>
                <div className="grid grid-cols-2 gap-2 mb-3">
                  <div className="border border-[#E0E0E0] rounded-xl p-3">
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-[10px] text-[#666] font-medium">Services</span>
                      <span className="text-[9px] text-[#999]">Target: 8/day</span>
                    </div>
                    <p className="text-2xl font-bold text-[#0052CC] tabular-nums">{bmftKpi.services_today || stats.completed_today || 0}<span className="text-sm text-[#999] font-normal">/8</span></p>
                    <div className="w-full bg-[#E0E0E0] rounded-full h-1.5 mt-1.5">
                      <div className="h-1.5 rounded-full bg-[#0052CC] transition-all" style={{ width: `${Math.min(100, ((bmftKpi.services_today || stats.completed_today || 0) / 8) * 100)}%` }} />
                    </div>
                    {(bmftKpi.services_today || 0) >= 8 && <p className="text-[10px] text-[#006D3B] font-bold mt-1">+2,500 MMK Bonus ✓</p>}
                  </div>
                  <div className="border border-[#E0E0E0] rounded-xl p-3">
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-[10px] text-[#666] font-medium">New Installs</span>
                      <span className="text-[9px] text-[#999]">Target: 4/day</span>
                    </div>
                    <p className="text-2xl font-bold text-[#1B5E20] tabular-nums">{bmftKpi.installs_today || 0}<span className="text-sm text-[#999] font-normal">/4</span></p>
                    <div className="w-full bg-[#E0E0E0] rounded-full h-1.5 mt-1.5">
                      <div className="h-1.5 rounded-full bg-[#1B5E20] transition-all" style={{ width: `${Math.min(100, ((bmftKpi.installs_today || 0) / 4) * 100)}%` }} />
                    </div>
                    {(bmftKpi.installs_today || 0) >= 4 && <p className="text-[10px] text-[#006D3B] font-bold mt-1">+5,000 MMK Bonus ✓</p>}
                  </div>
                </div>
                {/* Total Bonus */}
                <div className="bg-gradient-to-r from-[#1B5E20] to-[#2E7D32] rounded-xl p-3 text-white text-center">
                  <p className="text-[10px] opacity-80 mb-0.5">Today's Total Bonus</p>
                  <p className="text-2xl font-bold tabular-nums">{((bmftKpi.services_today >= 8 ? 2500 : 0) + (bmftKpi.installs_today >= 4 ? 5000 : 0)).toLocaleString()} <span className="text-sm font-normal opacity-80">MMK</span></p>
                  <p className="text-[10px] opacity-70 mt-0.5">Services ≥8: +2,500 · Installs ≥4: +5,000</p>
                </div>
              </div>

              {/* Monthly Summary */}
              <div className="bg-white rounded-xl p-4 shadow-sm">
                <div className="flex items-center justify-between mb-3">
                  <span className="text-sm font-bold">This Month</span>
                  <span className="text-[10px] text-[#999]">{new Date().toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}</span>
                </div>
                <div className="grid grid-cols-3 gap-2 mb-3">
                  <div className="text-center bg-[#F5F6F8] rounded-xl p-2">
                    <p className="text-lg font-bold text-[#0052CC] tabular-nums">{bmftMonthly.total_jobs || 0}</p>
                    <p className="text-[9px] text-[#999] uppercase">Total Jobs</p>
                  </div>
                  <div className="text-center bg-[#F5F6F8] rounded-xl p-2">
                    <p className="text-lg font-bold text-[#006D3B] tabular-nums">{bmftMonthly.completed || 0}</p>
                    <p className="text-[9px] text-[#999] uppercase">Completed</p>
                  </div>
                  <div className="text-center bg-[#F5F6F8] rounded-xl p-2">
                    <p className="text-lg font-bold text-[#1B5E20] tabular-nums">{(bmftMonthly.total_bonus || 0).toLocaleString()}</p>
                    <p className="text-[9px] text-[#999] uppercase">Bonus MMK</p>
                  </div>
                </div>
                {/* Fault History */}
                {bmftMonthly.fault_history && bmftMonthly.fault_history.length > 0 && (
                  <>
                    <p className="text-xs font-bold text-[#BA1A1A] mb-2">Fault History This Month</p>
                    {bmftMonthly.fault_history.slice(0, 5).map((f, i) => (
                      <div key={i} className="flex items-center justify-between py-1.5 border-b border-[#F0F0F0] last:border-0">
                        <div>
                          <p className="text-xs font-medium">{f.task_type || 'Fault'}{f.issue_type ? ` — ${f.issue_type}` : ''}</p>
                          <p className="text-[10px] text-[#999]">{f.location || f.township} · {f.task_id}</p>
                        </div>
                        <div className="text-right">
                          <p className="text-[10px] font-bold text-[#BA1A1A]">{f.created_at ? new Date(f.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : ''}</p>
                          <p className="text-[9px] text-[#999]">{f.status}</p>
                        </div>
                      </div>
                    ))}
                  </>
                )}
              </div>

              {/* Build Summary */}
              <div className="bg-white rounded-xl p-4 shadow-sm" style={{border: '2px solid #1B5E20'}}>
                <div className="flex items-center justify-between mb-2">
                  <span className="flex items-center gap-1.5 text-sm font-medium">Build &amp; Maintenance Jobs</span>
                  <span className="text-xs px-2 py-0.5 rounded-full font-bold" style={{background:'#E8F5E9',color:'#1B5E20'}}>BMFT</span>
                </div>
                <div className="grid grid-cols-3 gap-2 mt-2">
                  <div className="text-center"><p className="text-lg font-bold" style={{color:'#F57F17'}}>{stats.pending || 0}</p><p className="text-[9px] text-gray-400 uppercase">Pending</p></div>
                  <div className="text-center"><p className="text-lg font-bold" style={{color:'#0052CC'}}>{stats.in_progress || 0}</p><p className="text-[9px] text-gray-400 uppercase">In Progress</p></div>
                  <div className="text-center"><p className="text-lg font-bold" style={{color:'#006D3B'}}>{stats.completed_today || 0}</p><p className="text-[9px] text-gray-400 uppercase">Done Today</p></div>
                </div>
                <p className="text-[10px] text-gray-400 mt-2 text-center">This month: {stats.total_this_month || 0} total jobs</p>
              </div>
            </>
          ) : (
          <div className={`bg-white rounded-xl p-4 shadow-sm ${target.target_met ? 'border-2 border-[#006D3B]' : ''}`}>
            <div className="flex items-center justify-between mb-2">
              <span className="flex items-center gap-1.5 text-sm font-medium"><Target size={16} className="text-[#0052CC]" />Daily Target</span>
              <span className="text-lg font-bold tabular-nums" style={{ color: target.target_met ? '#006D3B' : '#0052CC' }}>{target.progress_pct || 0}%</span>
            </div>
            <div className="w-full bg-[#E0E0E0] rounded-full h-2"><div className="h-2 rounded-full transition-all" style={{ width: `${target.progress_pct || 0}%`, background: target.target_met ? '#006D3B' : '#0052CC' }} /></div>
            <p className="text-[10px] text-[#999] mt-1">{target.installations || 0} installs + {target.services || 0} services = {target.points || 0}/{target.target_points || 8} pts</p>
          </div>
          )}

          {/* Quick Actions */}
          <div className="grid grid-cols-3 gap-2">
            <button onClick={() => setScreen('jobs')} className="bg-white rounded-xl p-4 text-center shadow-sm active:bg-[#F5F5F5]"><Clock size={24} className="mx-auto text-[#0052CC] mb-1" /><span className="text-xs font-medium">My Jobs</span></button>
            <button onClick={manualSync} disabled={syncing} className="bg-white rounded-xl p-4 text-center shadow-sm active:bg-[#F5F5F5]"><ArrowClockwise size={24} className={`mx-auto text-[#006D3B] mb-1 ${syncing ? 'animate-spin' : ''}`} /><span className="text-xs font-medium">Sync Now</span></button>
            <button onClick={() => setScreen('sync')} className="bg-white rounded-xl p-4 text-center shadow-sm active:bg-[#F5F5F5]"><Database size={24} className="mx-auto text-[#7C4DFF] mb-1" /><span className="text-xs font-medium">Sync Info</span></button>
          </div>

          {/* PWA Install Banner */}
          {installPrompt && !isInstalled && (
            <button onClick={handleInstall} className="w-full bg-[#006D3B] text-white rounded-xl p-3 flex items-center gap-3 active:bg-[#004D2A] shadow-sm">
              <div className="w-10 h-10 bg-white/20 rounded-lg flex items-center justify-center flex-shrink-0"><CloudArrowUp size={22} weight="fill" /></div>
              <div className="text-left flex-1"><p className="text-sm font-bold">Install App</p><p className="text-[10px] opacity-80">Add to home screen for offline access</p></div>
              <ArrowClockwise size={16} />
            </button>
          )}
          {isInstalled && (
            <div className="bg-[#E8F5E9] rounded-xl p-2.5 flex items-center gap-2 text-xs text-[#1B5E20]">
              <CheckCircle size={14} weight="fill" /><span className="font-medium">App installed on device</span>
            </div>
          )}

          {/* My Jobs / BMFT Tasks */}
          <p className="text-sm font-medium pt-1">{user?.department === 'BMFT' ? 'My Tasks' : "Today's Jobs"}</p>
          {(user?.department === 'BMFT' ? tasks : tasks.filter(t => !['Completed', 'Cancelled'].includes(t.status))).map(t => (
            <button key={t.task_id} onClick={() => { setSelectedTask(t); setScreen('detail'); setBmftProgress(t.progress || 0); setReportForm({ ...reportForm, cust_phone: t.customer_phone || '', street: t.address || '' }); }}
              className={`w-full bg-white rounded-xl p-4 text-left shadow-sm active:bg-[#F5F5F5] ${t.status === 'Completed' ? 'border-l-4 border-l-[#006D3B]' : t.status === 'Pending Approval' ? 'border-l-4 border-l-[#F57F17]' : t.status === 'In Progress' ? 'border-l-4 border-l-[#0052CC]' : ''}`}>
              <div className="flex items-center justify-between mb-1">
                <span className="text-xs font-bold text-[#0052CC]">{t.task_id}</span>
                <div className="flex items-center gap-1.5">
                  {t._localUpdate && <span className="text-[8px] bg-[#FFF8E1] text-[#F57F17] px-1.5 py-0.5 rounded font-bold">LOCAL</span>}
                  <span className="text-[10px] font-bold px-2 py-0.5 rounded-full" style={{ color: t.status === 'Completed' ? '#1B5E20' : t.status === 'In Progress' ? '#283593' : t.status === 'Pending Approval' ? '#F57F17' : '#4527A0', background: t.status === 'Completed' ? '#E8F5E9' : t.status === 'In Progress' ? '#E8EAF6' : t.status === 'Pending Approval' ? '#FFF8E1' : '#EDE7F6' }}>{t.status}</span>
                </div>
              </div>
              {user?.department === 'BMFT' ? (
                <>
                  <p className="text-sm font-medium">{t.task_type || 'BMFT Task'}{t.issue_type ? ` — ${t.issue_type}` : ''}</p>
                  <p className="text-xs text-[#999]">{t.location || t.township}{t.link_name ? ` · ${t.link_name}` : ''}</p>
                  {t.progress > 0 && <div className="mt-1.5"><div className="w-full bg-[#E0E0E0] rounded-full h-1"><div className="h-1 rounded-full bg-[#0052CC]" style={{ width: `${t.progress}%` }} /></div><p className="text-[9px] text-[#999] mt-0.5">{t.progress}% complete</p></div>}
                </>
              ) : (
                <><p className="text-sm font-medium">{t.customer_name || 'Customer'}</p><p className="text-xs text-[#999]">{t.township} · {t.task_type}</p></>
              )}
            </button>
          ))}
          {(user?.department === 'BMFT' ? tasks : tasks.filter(t => !['Completed', 'Cancelled'].includes(t.status))).length === 0 && <div className="bg-white rounded-xl p-8 text-center text-sm text-[#999]">{online ? 'No tasks assigned' : 'No cached tasks — connect to download'}</div>}
        </div>
      )}

      {/* JOB LIST */}
      {screen === 'jobs' && (
        <div className="p-4 space-y-2">
          {tasks.map(t => (
            <button key={t.task_id} onClick={() => { setSelectedTask(t); setScreen('detail'); setBmftProgress(t.progress || 0); setReportForm({ ...reportForm, cust_phone: t.customer_phone || '', street: t.address || '' }); }}
              className={`w-full bg-white rounded-xl p-4 text-left shadow-sm active:bg-[#F5F5F5] ${t.status === 'Completed' ? 'border-l-4 border-l-[#006D3B]' : t.status === 'Pending Approval' ? 'border-l-4 border-l-[#F57F17]' : t.status === 'In Progress' ? 'border-l-4 border-l-[#0052CC]' : ''}`}>
              <div className="flex justify-between mb-1">
                <span className="text-xs font-bold text-[#0052CC]">{t.task_id}</span>
                <div className="flex items-center gap-1.5">
                  {t._localUpdate && <span className="text-[8px] bg-[#FFF8E1] text-[#F57F17] px-1.5 py-0.5 rounded font-bold">LOCAL</span>}
                  <span className="text-[10px] font-bold px-2 py-0.5 rounded-full" style={{ color: t.status === 'Completed' ? '#1B5E20' : t.status === 'In Progress' ? '#283593' : t.status === 'Pending Approval' ? '#F57F17' : '#4527A0', background: t.status === 'Completed' ? '#E8F5E9' : t.status === 'In Progress' ? '#E8EAF6' : t.status === 'Pending Approval' ? '#FFF8E1' : '#EDE7F6' }}>{t.status}</span>
                </div>
              </div>
              {user?.department === 'BMFT' ? (
                <>
                  <p className="text-sm font-medium">{t.task_type || 'BMFT Task'}{t.issue_type ? ` — ${t.issue_type}` : ''}</p>
                  <p className="text-xs text-[#999] flex items-center gap-1"><MapPin size={12} />{t.location || t.township}{t.link_name ? ` · ${t.link_name}` : ''}</p>
                  {t.progress > 0 && <div className="mt-1.5"><div className="w-full bg-[#E0E0E0] rounded-full h-1"><div className="h-1 rounded-full bg-[#0052CC]" style={{ width: `${t.progress}%` }} /></div><p className="text-[9px] text-[#999] mt-0.5">{t.progress}% complete</p></div>}
                </>
              ) : (
                <><p className="text-sm font-medium">{t.customer_name} — {t.task_type}</p><p className="text-xs text-[#999] flex items-center gap-1"><MapPin size={12} />{t.township}</p></>
              )}
            </button>
          ))}
          {tasks.length === 0 && <div className="bg-white rounded-xl p-8 text-center text-sm text-[#999]">No tasks cached</div>}
        </div>
      )}

      {/* JOB DETAIL */}
      {screen === 'detail' && selectedTask && (
        <div className="p-4 space-y-3">
          {/* Task Info Card */}
          <div className="bg-white rounded-xl p-4 shadow-sm">
            <div className="flex items-start justify-between mb-1">
              <span className="text-xs font-bold text-[#0052CC]">{selectedTask.task_id}</span>
              <span className="text-[10px] font-bold px-2 py-0.5 rounded-full" style={{ color: selectedTask.status === 'Completed' ? '#1B5E20' : selectedTask.status === 'In Progress' ? '#283593' : selectedTask.status === 'Pending Approval' ? '#F57F17' : '#4527A0', background: selectedTask.status === 'Completed' ? '#E8F5E9' : selectedTask.status === 'In Progress' ? '#E8EAF6' : selectedTask.status === 'Pending Approval' ? '#FFF8E1' : '#EDE7F6' }}>{selectedTask.status}</span>
            </div>
            {user?.department === 'BMFT' ? (
              <>
                <p className="text-base font-bold">{selectedTask.task_type || 'BMFT Task'}</p>
                {selectedTask.issue_type && <p className="text-xs text-[#E65100] font-medium mt-0.5">⚠ {selectedTask.issue_type}</p>}
                {selectedTask.link_name && <p className="text-sm text-[#666] mt-1">🔗 Link: <span className="font-medium">{selectedTask.link_name}</span></p>}
                {selectedTask.location && <p className="text-sm text-[#666] flex items-center gap-1 mt-0.5"><MapPin size={14} />{selectedTask.location}{selectedTask.township ? ` · ${selectedTask.township}` : ''}</p>}
                {selectedTask.down_time && <p className="text-xs text-[#999] mt-0.5">Down since: {new Date(selectedTask.down_time).toLocaleString()}</p>}
                {selectedTask.impacted_users && <p className="text-xs text-[#BA1A1A] font-medium mt-0.5">👥 Impacted: {selectedTask.impacted_users}</p>}
                {selectedTask.priority && selectedTask.priority !== 'Normal' && <span className="inline-block mt-1 text-[10px] bg-[#FFEBEE] text-[#BA1A1A] px-2 py-0.5 rounded-full font-bold">{selectedTask.priority}</span>}
                {selectedTask.description && <p className="text-xs text-[#666] mt-2 border-t border-[#F0F0F0] pt-2">{selectedTask.description}</p>}
                {selectedTask.progress > 0 && (
                  <div className="mt-2">
                    <div className="flex justify-between text-[10px] text-[#666] mb-1"><span>Progress</span><span className="font-bold">{selectedTask.progress}%</span></div>
                    <div className="w-full bg-[#E0E0E0] rounded-full h-2"><div className="h-2 rounded-full bg-[#0052CC] transition-all" style={{ width: `${selectedTask.progress}%` }} /></div>
                  </div>
                )}
              </>
            ) : (
              <>
                <p className="text-lg font-bold">{selectedTask.customer_name}</p>
                <p className="text-sm text-[#666] flex items-center gap-1"><MapPin size={14} />{selectedTask.township} {selectedTask.address}</p>
                {selectedTask.customer_phone && <p className="text-sm text-[#666] flex items-center gap-1"><Phone size={14} />{selectedTask.customer_phone}</p>}
              </>
            )}
          </div>

          {/* IMFT action buttons only */}
          {user?.department !== 'BMFT' && (
            <div className="grid grid-cols-3 gap-2">
              <a href={`tel:${selectedTask.customer_phone}`} className="bg-[#E3F2FD] rounded-xl p-3 text-center"><Phone size={20} className="mx-auto text-[#0052CC]" /><p className="text-[10px] font-medium text-[#0052CC] mt-1">Call</p></a>
              <button className="bg-[#E8F5E9] rounded-xl p-3 text-center"><MapPin size={20} className="mx-auto text-[#006D3B]" /><p className="text-[10px] font-medium text-[#006D3B] mt-1">Navigate</p></button>
              <button onClick={capturePhoto} className="bg-[#FFF3E0] rounded-xl p-3 text-center"><Lightning size={20} className="mx-auto text-[#E65100]" /><p className="text-[10px] font-medium text-[#E65100] mt-1">Photo</p></button>
            </div>
          )}

          {/* ===== BMFT WORKFLOW ===== */}
          {user?.department === 'BMFT' && (
            <>
              {/* BMFT: Pending/Assigned — Start Job */}
              {(selectedTask.status === 'Pending' || selectedTask.status === 'Assigned') && (
                <div className="space-y-3">
                  <div className="bg-[#E3F2FD] rounded-xl p-4 text-center">
                    <p className="text-sm font-bold text-[#0052CC]">Ready to Start</p>
                    <p className="text-xs text-[#666] mt-1">Tap below to begin work on this task</p>
                  </div>
                  <button onClick={() => bmftStartJob(selectedTask)} disabled={loading} className="w-full bg-[#0052CC] text-white rounded-2xl py-4 font-bold active:bg-[#003D99] flex items-center justify-center gap-2">
                    {loading ? <><span className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />Starting...</> : <>▶ Start Job</>}
                  </button>
                </div>
              )}

              {/* BMFT: In Progress — Onsite Update + Completion Report */}
              {selectedTask.status === 'In Progress' && (
                <div className="space-y-3">
                  {/* Previous onsite updates */}
                  {selectedTask.onsite_updates && selectedTask.onsite_updates.length > 0 && (
                    <div className="bg-white rounded-xl p-4 shadow-sm">
                      <p className="text-xs font-bold text-[#666] mb-2">📋 Previous Updates ({selectedTask.onsite_updates.length})</p>
                      {selectedTask.onsite_updates.slice(-3).map((u, i) => (
                        <div key={i} className="border-l-2 border-[#0052CC] pl-3 mb-2">
                          <p className="text-xs font-medium">{u.description}</p>
                          <p className="text-[10px] text-[#999]">{u.progress}% · {u.updated_by} · {u.updated_at ? new Date(u.updated_at).toLocaleTimeString() : ''}</p>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Progress Update */}
                  <div className="bg-white rounded-xl p-4 shadow-sm space-y-3">
                    <p className="font-bold text-sm">📊 Add Progress Update</p>
                    <div>
                      <div className="flex justify-between text-xs text-[#666] mb-1"><span>Progress</span><span className="font-bold text-[#0052CC]">{bmftProgress}%</span></div>
                      <input type="range" min="0" max="100" step="5" value={bmftProgress} onChange={e => setBmftProgress(Number(e.target.value))} className="w-full accent-[#0052CC]" />
                      <div className="flex justify-between text-[9px] text-[#999] mt-0.5"><span>0%</span><span>25%</span><span>50%</span><span>75%</span><span>100%</span></div>
                    </div>
                    <div><p className="text-xs font-medium text-[#666] mb-1">Update Note *</p><textarea value={bmftOnsiteNote} onChange={e => setBmftOnsiteNote(e.target.value)} className="w-full bg-[#F5F6F8] rounded-xl px-3 py-2.5 text-sm border border-[#E0E0E0] outline-none focus:border-[#0052CC] min-h-[60px] resize-none" placeholder="Describe current work status..." /></div>
                    <div className="border border-dashed border-[#0052CC] rounded-xl p-3">
                      <p className="text-xs font-bold text-[#0052CC] mb-2">📷 Photos ({photos.length})</p>
                      <div className="grid grid-cols-4 gap-2 mb-2">{photos.map((p, i) => (<div key={i} className="relative"><img src={p.data} alt="" className="w-full h-14 object-cover rounded-lg" /><button onClick={() => setPhotos(prev => prev.filter((_, j) => j !== i))} className="absolute -top-1 -right-1 w-4 h-4 bg-[#BA1A1A] text-white rounded-full text-[8px] flex items-center justify-center">×</button></div>))}</div>
                      <button onClick={capturePhoto} className="w-full bg-[#E3F2FD] text-[#0052CC] rounded-lg py-2 text-xs font-bold active:bg-[#BBDEFB]">+ Capture Photo</button>
                    </div>
                    <button onClick={bmftAddOnsiteUpdate} disabled={bmftSubmitting} className="w-full bg-[#0052CC] text-white rounded-xl py-3 font-bold text-sm active:bg-[#003D99] flex items-center justify-center gap-2">
                      <ArrowClockwise size={16} className={bmftSubmitting ? 'animate-spin' : ''} />{bmftSubmitting ? 'Saving...' : 'Save Progress Update'}
                    </button>
                  </div>

                  {/* Completion Report */}
                  <div className="bg-white rounded-xl p-4 shadow-sm space-y-3">
                    <p className="font-bold text-sm">✅ Completion Report</p>
                    <div className="grid grid-cols-2 gap-2">
                      <div><p className="text-[10px] text-[#666] mb-1">Error Type</p><input value={bmftReport.error_type} onChange={e => setBmftReport({...bmftReport, error_type: e.target.value})} className="w-full bg-[#F5F6F8] rounded-xl px-3 py-2 text-sm border border-[#E0E0E0] outline-none focus:border-[#0052CC]" placeholder="e.g. Fiber Cut" /></div>
                      <div><p className="text-[10px] text-[#666] mb-1">FAT Name</p><input value={bmftReport.fat_name} onChange={e => setBmftReport({...bmftReport, fat_name: e.target.value})} className="w-full bg-[#F5F6F8] rounded-xl px-3 py-2 text-sm border border-[#E0E0E0] outline-none focus:border-[#0052CC]" placeholder="FAT-001" /></div>
                      <div><p className="text-[10px] text-[#666] mb-1">FAT Location</p><input value={bmftReport.fat_location} onChange={e => setBmftReport({...bmftReport, fat_location: e.target.value})} className="w-full bg-[#F5F6F8] rounded-xl px-3 py-2 text-sm border border-[#E0E0E0] outline-none focus:border-[#0052CC]" placeholder="Location" /></div>
                      <div><p className="text-[10px] text-[#666] mb-1">Fiber Used (m)</p><input value={bmftReport.fiber_meter_used} onChange={e => setBmftReport({...bmftReport, fiber_meter_used: e.target.value})} type="number" className="w-full bg-[#F5F6F8] rounded-xl px-3 py-2 text-sm border border-[#E0E0E0] outline-none focus:border-[#0052CC]" placeholder="0" /></div>
                      <div><p className="text-[10px] text-[#666] mb-1">ONU S/N</p><input value={bmftReport.onu_sn} onChange={e => setBmftReport({...bmftReport, onu_sn: e.target.value})} className="w-full bg-[#F5F6F8] rounded-xl px-3 py-2 text-sm border border-[#E0E0E0] outline-none focus:border-[#0052CC]" placeholder="HWTC-xxx" /></div>
                      <div><p className="text-[10px] text-[#666] mb-1">Remark</p><input value={bmftReport.remark} onChange={e => setBmftReport({...bmftReport, remark: e.target.value})} className="w-full bg-[#F5F6F8] rounded-xl px-3 py-2 text-sm border border-[#E0E0E0] outline-none focus:border-[#0052CC]" placeholder="Optional" /></div>
                    </div>
                    <div><p className="text-xs font-medium text-[#666] mb-1">Work Notes *</p><textarea value={bmftReport.work_notes} onChange={e => setBmftReport({...bmftReport, work_notes: e.target.value})} className="w-full bg-[#F5F6F8] rounded-xl px-3 py-2.5 text-sm border border-[#E0E0E0] outline-none focus:border-[#0052CC] min-h-[70px] resize-none" placeholder="Describe all work performed, materials used, and final status..." /></div>
                    <div className="bg-[#FFF8E1] rounded-lg p-2.5 text-xs text-[#E65100] flex items-start gap-2"><Warning size={14} className="flex-shrink-0 mt-0.5" /><span>Submitting will set progress to 100% and send for supervisor approval.</span></div>
                    <button onClick={bmftSubmitReport} disabled={bmftSubmitting} className="w-full bg-[#1B5E20] text-white rounded-2xl py-4 font-bold active:bg-[#145218] flex items-center justify-center gap-2">
                      <CloudArrowUp size={20} />{bmftSubmitting ? 'Submitting...' : online ? 'Submit Completion Report' : 'Save Report (Offline)'}
                    </button>
                  </div>
                </div>
              )}

              {selectedTask.status === 'Pending Approval' && (
                <div className="bg-[#FFF8E1] rounded-xl p-6 text-center">
                  <Warning size={32} className="mx-auto text-[#F57F17] mb-2" />
                  <p className="font-bold text-[#F57F17]">Awaiting Supervisor Approval</p>
                  <p className="text-xs text-[#999] mt-1">Your completion report has been submitted</p>
                </div>
              )}
              {selectedTask.status === 'Completed' && (
                <div className="bg-[#E8F5E9] rounded-xl p-6 text-center">
                  <CheckCircle size={32} weight="fill" className="mx-auto text-[#006D3B] mb-2" />
                  <p className="font-bold text-[#006D3B]">Job Completed & Approved</p>
                  {selectedTask.completion_report?.work_notes && <p className="text-xs text-[#666] mt-2 text-left border-t border-[#E0E0E0] pt-2">{selectedTask.completion_report.work_notes}</p>}
                </div>
              )}
            </>
          )}

          {/* ===== IMFT/NOC WORKFLOW (unchanged) ===== */}
          {user?.department !== 'BMFT' && (
            <>
              {selectedTask.status === 'Assigned' && (
                <div className="space-y-3">
                  <div className="bg-[#E3F2FD] rounded-xl p-4 text-center">
                    <MapPin size={32} className="mx-auto text-[#0052CC] mb-2" />
                    <p className="text-sm font-bold text-[#0052CC]">GPS Check-In Required</p>
                    <p className="text-xs text-[#666] mt-1">Your location will be verified against the customer address</p>
                  </div>
                  <button onClick={() => gpsCheckIn(selectedTask)} disabled={gettingGPS || loading} className="w-full bg-[#0052CC] text-white rounded-2xl py-4 text-center font-bold text-base active:bg-[#003D99] flex items-center justify-center gap-2">
                    {gettingGPS ? <><span className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />Getting GPS...</> : <><MapPin size={20} weight="fill" />Start Job (GPS Check-In)</>}
                  </button>
                </div>
              )}
              {(selectedTask.gps_checkin || gpsStatus) && selectedTask.status !== 'Assigned' && (
                <div className={`rounded-xl p-3 ${(selectedTask.gps_checkin?.within_range !== false && gpsStatus?.within_range !== false) ? 'bg-[#E8F5E9]' : 'bg-[#FFF3E0]'}`}>
                  <div className="flex items-center gap-2 mb-1">{(selectedTask.gps_checkin?.within_range !== false) ? <CheckCircle size={16} className="text-[#006D3B]" weight="fill" /> : <Warning size={16} className="text-[#E65100]" />}<span className="text-xs font-bold">{(selectedTask.gps_checkin?.within_range !== false) ? 'Location Verified' : 'Location Warning'}</span></div>
                  <p className="text-[10px] text-[#666]">{(gpsStatus || selectedTask.gps_checkin)?.distance_m !== null ? `Distance: ${(gpsStatus || selectedTask.gps_checkin)?.distance_m}m` : 'GPS captured'} · {selectedTask.gps_checkin?.timestamp ? new Date(selectedTask.gps_checkin.timestamp).toLocaleTimeString() : ''}</p>
                </div>
              )}
              {selectedTask.status === 'In Progress' && (
                <div className="bg-white rounded-xl p-4 shadow-sm space-y-4">
                  <p className="font-bold text-sm">Job Completion Report</p>
                  <div className="border border-dashed border-[#0052CC] rounded-xl p-3">
                    <p className="text-xs font-bold text-[#0052CC] mb-2 flex items-center gap-1">📷 Photo Evidence ({photos.length})</p>
                    <div className="grid grid-cols-4 gap-2 mb-2">{photos.map((p, i) => (<div key={i} className="relative"><img src={p.data} alt="" className="w-full h-16 object-cover rounded-lg" /><button onClick={() => setPhotos(prev => prev.filter((_, j) => j !== i))} className="absolute -top-1 -right-1 w-4 h-4 bg-[#BA1A1A] text-white rounded-full text-[8px] flex items-center justify-center">×</button></div>))}</div>
                    <button onClick={capturePhoto} className="w-full bg-[#E3F2FD] text-[#0052CC] rounded-lg py-2.5 text-xs font-bold active:bg-[#BBDEFB]">+ Capture Photo</button>
                    <p className="text-[9px] text-[#999] mt-1">Required: House exterior, ONU device, fiber connection</p>
                  </div>
                  <div className="border border-[#E0E0E0] rounded-xl p-3">
                    <p className="text-xs font-bold text-[#7C4DFF] mb-2 flex items-center gap-1"><Lightning size={14} />Signal Levels</p>
                    <div className="grid grid-cols-2 gap-2">
                      <div><p className="text-[10px] text-[#666] mb-1">OLT Port</p><input value={signalData.olt_port} onChange={e => setSignalData({...signalData, olt_port: e.target.value})} className="md3-input text-sm" placeholder="1/1/1" /></div>
                      <div><p className="text-[10px] text-[#666] mb-1">ONU Serial</p><input value={signalData.onu_serial} onChange={e => setSignalData({...signalData, onu_serial: e.target.value})} className="md3-input text-sm tabular-nums" placeholder="HWTC-xxx" /></div>
                      <div><p className="text-[10px] text-[#666] mb-1">RX Level (dBm)</p><input value={signalData.rx_level} onChange={e => setSignalData({...signalData, rx_level: e.target.value})} className="md3-input text-sm tabular-nums text-center font-bold" type="number" step="0.1" placeholder="-22.5" /></div>
                      <div><p className="text-[10px] text-[#666] mb-1">TX Level (dBm)</p><input value={signalData.tx_level} onChange={e => setSignalData({...signalData, tx_level: e.target.value})} className="md3-input text-sm tabular-nums text-center font-bold" type="number" step="0.1" placeholder="2.1" /></div>
                    </div>
                  </div>
                  <div className="border border-[#E0E0E0] rounded-xl p-3">
                    <p className="text-xs font-bold text-[#006D3B] mb-2 flex items-center gap-1"><CheckCircle size={14} />Customer Verification</p>
                    <div><p className="text-[10px] text-[#666] mb-1">Phone Number</p><input value={reportForm.cust_phone} onChange={e => setReportForm({ ...reportForm, cust_phone: e.target.value })} className="md3-input text-sm" /></div>
                    <div className="grid grid-cols-2 gap-2 mt-2"><div><p className="text-[10px] text-[#666] mb-1">House No</p><input value={reportForm.house_no} onChange={e => setReportForm({ ...reportForm, house_no: e.target.value })} className="md3-input text-sm" /></div><div><p className="text-[10px] text-[#666] mb-1">Street</p><input value={reportForm.street} onChange={e => setReportForm({ ...reportForm, street: e.target.value })} className="md3-input text-sm" /></div></div>
                  </div>
                  <div><p className="text-xs font-medium text-[#666] mb-1">Work Notes *</p><textarea value={reportForm.work_notes} onChange={e => setReportForm({ ...reportForm, work_notes: e.target.value })} className="md3-input min-h-[60px]" placeholder="Describe work performed..." /></div>
                  <div className="bg-[#FFEBEE] rounded-lg p-2.5 text-xs text-[#BA1A1A] flex items-start gap-2"><Warning size={16} className="flex-shrink-0 mt-0.5" /><div><p className="font-bold">GPS Proof-of-Work Required</p><p className="text-[10px] mt-0.5">Your GPS location, photos, and signal data will be captured and sent to NOC for verification.</p></div></div>
                  <button onClick={submitWithGPS} disabled={loading} className="w-full bg-[#E65100] text-white rounded-2xl py-4 font-bold active:bg-[#BF360C] flex items-center justify-center gap-2">
                    <CloudArrowUp size={20} />{loading ? 'Submitting...' : online ? 'Submit with GPS Proof' : 'Save Report (Offline)'}
                  </button>
                </div>
              )}
              {selectedTask.status === 'Pending Approval' && (<div className="bg-[#FFF8E1] rounded-xl p-6 text-center"><Warning size={32} className="mx-auto text-[#F57F17] mb-2" /><p className="font-bold text-[#F57F17]">Awaiting NOC Approval</p></div>)}
              {selectedTask.status === 'Completed' && (<div className="bg-[#E8F5E9] rounded-xl p-6 text-center"><CheckCircle size={32} weight="fill" className="mx-auto text-[#006D3B] mb-2" /><p className="font-bold text-[#006D3B]">Job Completed & Approved</p></div>)}
            </>
          )}
        </div>
      )}

      {/* SYNC STATUS SCREEN */}
      {screen === 'sync' && (
        <div className="p-4 space-y-3">
          {/* Sync Overview */}
          <div className="bg-white rounded-xl p-4 shadow-sm">
            <div className="flex items-center gap-3 mb-3">
              <div className={`w-12 h-12 rounded-full flex items-center justify-center ${online ? 'bg-[#E8F5E9]' : 'bg-[#FFEBEE]'}`}>
                {online ? <WifiHigh size={24} className="text-[#006D3B]" /> : <WifiSlash size={24} className="text-[#BA1A1A]" />}
              </div>
              <div>
                <p className="font-bold">{online ? 'Online' : 'Offline'}</p>
                <p className="text-xs text-[#666]">{online ? 'Connected to server' : 'Working with local data'}</p>
              </div>
            </div>
            <div className="grid grid-cols-3 gap-2 text-center">
              <div className="bg-[var(--md-surface-1)] rounded-lg p-2">
                <p className="text-lg font-bold tabular-nums" style={{ color: SYNC_COLORS[syncStatus] }}>{pendingCount}</p>
                <p className="text-[9px] text-[#999]">Pending</p>
              </div>
              <div className="bg-[var(--md-surface-1)] rounded-lg p-2">
                <p className="text-lg font-bold tabular-nums text-[#0052CC]">{tasks.length}</p>
                <p className="text-[9px] text-[#999]">Cached Jobs</p>
              </div>
              <div className="bg-[var(--md-surface-1)] rounded-lg p-2">
                <p className="text-[10px] font-medium tabular-nums">{lastSync ? new Date(lastSync).toLocaleTimeString() : 'Never'}</p>
                <p className="text-[9px] text-[#999]">Last Sync</p>
              </div>
            </div>
          </div>

          {/* Sync Status Legend */}
          <div className="bg-white rounded-xl p-4 shadow-sm space-y-2">
            <p className="text-sm font-medium mb-2">Sync Indicators</p>
            {[{ color: '#006D3B', label: 'Green', desc: 'All data synced with server' }, { color: '#F57F17', label: 'Yellow', desc: 'Pending items in sync queue' }, { color: '#BA1A1A', label: 'Red', desc: 'Sync error — manual retry needed' }].map(i => (
              <div key={i.color} className="flex items-center gap-3">
                <div className="w-4 h-4 rounded-full flex-shrink-0" style={{ background: i.color }} />
                <div><p className="text-xs font-medium">{i.label}</p><p className="text-[10px] text-[#999]">{i.desc}</p></div>
              </div>
            ))}
          </div>

          {/* Manual Sync Button */}
          <button onClick={manualSync} disabled={syncing || !online} className={`w-full rounded-2xl py-4 font-bold text-center flex items-center justify-center gap-2 ${online ? 'bg-[#0052CC] text-white active:bg-[#003D99]' : 'bg-[#E0E0E0] text-[#999]'}`}>
            <ArrowClockwise size={20} className={syncing ? 'animate-spin' : ''} />
            {syncing ? 'Syncing...' : online ? 'Sync Now' : 'No Internet — Cannot Sync'}
          </button>

          {/* How it works */}
          <div className="bg-white rounded-xl p-4 shadow-sm">
            <p className="text-sm font-medium mb-2">How Offline Mode Works</p>
            <div className="space-y-2 text-xs text-[#666]">
              <p>1. Jobs are downloaded and cached when you're online</p>
              <p>2. You can view jobs, start work, and submit reports offline</p>
              <p>3. All changes are saved to your device's local database</p>
              <p>4. When internet returns, data auto-syncs to the server</p>
              <p>5. Photos are uploaded when bandwidth is available</p>
            </div>
          </div>

          {/* GPS Tracking Status */}
          {['BMFT', 'IMFT'].includes(user?.department) && (
            <div className="bg-white rounded-xl p-4 shadow-sm">
              <div className="flex items-center justify-between mb-3">
                <p className="text-sm font-medium">📍 GPS Tracking</p>
                <div className={`flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-bold ${trackingActive ? 'bg-[#E8F5E9] text-[#006D3B]' : 'bg-[#FFEBEE] text-[#BA1A1A]'}`}>
                  <div className={`w-2 h-2 rounded-full ${trackingActive ? 'bg-[#006D3B] animate-pulse' : 'bg-[#BA1A1A]'}`} />
                  {trackingActive ? 'Active' : 'Inactive'}
                </div>
              </div>
              <div className="grid grid-cols-2 gap-2 mb-3">
                <div className="bg-[#F5F6F8] rounded-lg p-2 text-center">
                  <p className="text-lg font-bold text-[#0052CC]">{trackingPings.length}</p>
                  <p className="text-[9px] text-[#999]">Pings Today</p>
                </div>
                <div className="bg-[#F5F6F8] rounded-lg p-2 text-center">
                  <p className="text-sm font-bold text-[#666]">{trackingPings.length > 0 ? new Date(trackingPings[trackingPings.length - 1].timestamp).toLocaleTimeString([], {hour: '2-digit', minute: '2-digit'}) : '--:--'}</p>
                  <p className="text-[9px] text-[#999]">Last Ping</p>
                </div>
              </div>
              <div className="flex gap-2">
                <button onClick={() => trackingActive ? stopTracking() : startTracking()} className={`flex-1 rounded-xl py-2.5 text-sm font-bold ${trackingActive ? 'bg-[#FFEBEE] text-[#BA1A1A]' : 'bg-[#E8F5E9] text-[#006D3B]'}`}>
                  {trackingActive ? '⏹ End Duty Tracking' : '▶ Start Duty Tracking'}
                </button>
                <button onClick={() => sendTrackingPing('duty')} className="flex-1 bg-[#E3F2FD] text-[#0052CC] rounded-xl py-2.5 text-sm font-bold">
                  📍 Ping Now
                </button>
              </div>
              <p className="text-[10px] text-[#999] mt-2 text-center">During duty only: records every 10 minutes while this app remains open · Visible to supervisors</p>
            </div>
          )}

          {/* App Version & Update */}
          <div className="bg-white rounded-xl p-4 shadow-sm">
            <p className="text-sm font-medium mb-3">App Version</p>
            <div className="grid grid-cols-3 gap-2 text-center mb-3">
              <div className="bg-[var(--md-surface-1)] rounded-lg p-2"><p className="text-sm font-bold tabular-nums text-[var(--md-primary)]">v{APP_VERSION}</p><p className="text-[9px] text-[#999]">Version</p></div>
              <div className="bg-[var(--md-surface-1)] rounded-lg p-2"><p className="text-sm font-bold tabular-nums">{BUILD_NUMBER}</p><p className="text-[9px] text-[#999]">Build</p></div>
              <div className="bg-[var(--md-surface-1)] rounded-lg p-2"><p className="text-[10px] font-medium tabular-nums">{localStorage.getItem('ota_last_check') ? new Date(localStorage.getItem('ota_last_check')).toLocaleDateString() : 'Never'}</p><p className="text-[9px] text-[#999]">Last Check</p></div>
            </div>
            <button onClick={checkOTAUpdate} disabled={checkingUpdate} className={`w-full rounded-xl py-3 text-sm font-bold flex items-center justify-center gap-2 ${updateInfo?.update_available ? 'bg-[#0052CC] text-white' : 'bg-[var(--md-surface-1)] text-[#666]'}`}>
              <ArrowClockwise size={16} className={checkingUpdate ? 'animate-spin' : ''} />
              {checkingUpdate ? 'Checking...' : updateInfo?.update_available ? `Update to v${updateInfo.latest_version}` : 'Check for Updates'}
            </button>
            {updateInfo?.update_available && (
              <div className="mt-3 p-3 bg-[#E3F2FD] rounded-lg">
                <div className="flex items-center justify-between mb-1">
                  <p className="text-xs font-bold text-[#0052CC]">v{updateInfo.latest_version} Available</p>
                  {updateInfo.mandatory && <span className="text-[9px] bg-[#FFEBEE] text-[#BA1A1A] px-2 py-0.5 rounded-full font-bold">MANDATORY</span>}
                </div>
                {updateInfo.release_notes && <p className="text-[11px] text-[#666]">{updateInfo.release_notes}</p>}
                <p className="text-[10px] text-[#999] mt-1">Released: {updateInfo.released_at ? new Date(updateInfo.released_at).toLocaleDateString() : ''}</p>
                <div className="flex gap-2 mt-2">
                  {updateInfo.download_url && <a href={updateInfo.download_url} target="_blank" rel="noreferrer" className="flex-1 text-center bg-[#0052CC] text-white text-xs py-2 rounded-lg font-bold">Download APK</a>}
                  <a href="https://play.google.com/store/apps/details?id=com.myanmarlink.field" target="_blank" rel="noreferrer" className="flex-1 text-center bg-[#006D3B] text-white text-xs py-2 rounded-lg font-bold">Play Store</a>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ===== READ-ONLY ODB CHECK SCREEN ===== */}
      {screen === 'odb-check' && (
        <div className="px-4 py-4 space-y-4 pb-24">
          <div className="bg-[#0052CC] text-white rounded-2xl p-4 shadow-sm">
            <div className="flex items-center gap-2"><Database size={20} weight="fill" /><p className="font-bold">ODB Check</p></div>
            <p className="text-xs text-white/70 mt-1">Check recorded active fault tasks, impact count, and service context. This screen does not change ODB data.</p>
          </div>
          <div className="bg-white rounded-2xl p-4 shadow-sm">
            <label className="text-xs font-bold text-[#666]">ODB / FAT name</label>
            <div className="flex gap-2 mt-2">
              <input value={odbCheckQuery} onChange={e => setOdbCheckQuery(e.target.value)} onKeyDown={e => e.key === 'Enter' && runOdbCheck()} placeholder="e.g. SPK-FAT-C05" className="flex-1 min-w-0 bg-[#F5F6F8] rounded-xl px-3 py-3 text-sm border border-[#E0E0E0] outline-none focus:border-[#0052CC]" />
              <button onClick={runOdbCheck} disabled={odbChecking} className="bg-[#0052CC] text-white px-4 rounded-xl text-sm font-bold disabled:opacity-50">{odbChecking ? 'Checking...' : 'Check'}</button>
            </div>
          </div>
          {odbCheck && (
            <div className="space-y-3">
              <div className={`rounded-2xl p-4 shadow-sm ${odbCheck.active_tasks > 0 ? 'bg-[#FFF3E0]' : 'bg-[#E8F5E9]'}`}>
                <div className="flex items-center justify-between"><div><p className="text-xs font-bold text-[#666]">{odbCheck.box_name}</p><p className="text-lg font-bold mt-1">{odbCheck.active_tasks > 0 ? 'Active fault task found' : 'No active down task recorded'}</p></div><span className={`text-[10px] font-bold px-2.5 py-1 rounded-full ${odbCheck.active_tasks > 0 ? 'bg-[#E65100] text-white' : 'bg-[#006D3B] text-white'}`}>{odbCheck.active_tasks > 0 ? `${odbCheck.active_tasks} ACTIVE` : 'CLEAR'}</span></div>
                <div className="grid grid-cols-2 gap-2 mt-3 text-center"><div className="bg-white/70 rounded-lg p-2"><p className="text-sm font-bold">{odbCheck.summary?.total ?? 0}</p><p className="text-[9px] text-[#666]">Recorded users</p></div><div className="bg-white/70 rounded-lg p-2"><p className="text-sm font-bold">{odbCheck.down_info?.impacted_count ?? 0}</p><p className="text-[9px] text-[#666]">Impacted users</p></div></div>
              </div>
              {odbCheck.down_info && <div className="bg-white rounded-2xl p-4 shadow-sm"><p className="text-xs font-bold text-[#E65100]">{odbCheck.down_info.ticket_id} · {odbCheck.down_info.status}</p><p className="text-sm font-bold mt-1">{odbCheck.down_info.issue_type || 'Network issue'}</p><p className="text-xs text-[#666] mt-1">Assigned: {odbCheck.down_info.assigned_to || 'Unassigned'} · Priority: {odbCheck.down_info.priority}</p></div>}
              <div className="bg-white rounded-2xl p-4 shadow-sm"><p className="text-xs font-bold text-[#666] mb-2">Service context</p><div className="grid grid-cols-3 gap-2 text-center"><div><p className="font-bold text-[#006D3B]">{odbCheck.summary?.active ?? 0}</p><p className="text-[9px] text-[#999]">Active</p></div><div><p className="font-bold text-[#E65100]">{odbCheck.summary?.needs_service ?? 0}</p><p className="text-[9px] text-[#999]">Service req.</p></div><div><p className="font-bold text-[#999]">{odbCheck.summary?.unknown ?? 0}</p><p className="text-[9px] text-[#999]">Unknown</p></div></div></div>
            </div>
          )}
        </div>
      )}

      {/* ===== SUPERVISOR DAILY TEAM MOVEMENT ===== */}
      {screen === 'team-movement' && isAdminOrSupervisor && (
        <div className="px-4 py-4 space-y-4 pb-24">
          <div className="bg-[#163E73] text-white rounded-2xl p-4 shadow-sm"><div className="flex items-center gap-2"><MapPin size={20} weight="fill" /><p className="font-bold">Daily Team Movement</p></div><p className="text-xs text-white/70 mt-1">Read-only IMFT/BMFT duty-time checkpoints recorded today.</p></div>
          <button onClick={loadTeamMovement} disabled={teamMovementLoading} className="w-full bg-white border border-[#DDE7F5] text-[#0052CC] rounded-xl py-3 text-sm font-bold flex items-center justify-center gap-2"><ArrowClockwise size={17} className={teamMovementLoading ? 'animate-spin' : ''} />{teamMovementLoading ? 'Refreshing...' : 'Refresh recorded locations'}</button>
          {teamMovement.length === 0 ? <div className="bg-white rounded-2xl p-8 text-center shadow-sm text-sm text-[#999]">No duty-time tracking points recorded for today.</div> : teamMovement.map(member => (
            <div key={member.user_id} className="bg-white rounded-2xl p-4 shadow-sm"><div className="flex justify-between gap-3"><div><p className="text-sm font-bold">{member.name || member.staff_id}</p><p className="text-[10px] text-[#666] mt-0.5">{member.department} · {member.ping_count} checkpoint{member.ping_count === 1 ? '' : 's'}</p></div><span className="bg-[#E8F5E9] text-[#006D3B] text-[10px] font-bold px-2 py-1 rounded-full h-fit">{member.active_minutes || 0} min</span></div><div className="mt-3 grid grid-cols-2 gap-2 text-xs"><div className="bg-[#F5F6F8] rounded-lg p-2"><p className="text-[9px] text-[#999]">First</p><p className="font-bold mt-0.5">{member.first_ping ? new Date(member.first_ping).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : '—'}</p></div><div className="bg-[#F5F6F8] rounded-lg p-2"><p className="text-[9px] text-[#999]">Last</p><p className="font-bold mt-0.5">{member.last_ping ? new Date(member.last_ping).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : '—'}</p></div></div>{member.last_lat != null && <p className="mt-3 text-[10px] text-[#666]">Last recorded point: {Number(member.last_lat).toFixed(5)}, {Number(member.last_lng).toFixed(5)} · ±{Math.round(member.last_accuracy || 0)}m</p>}</div>
          ))}
        </div>
      )}

      {/* ===== MAP SCREEN ===== */}
      {screen === 'map' && (
        <div className="flex flex-col" style={{ height: 'calc(100vh - 96px)' }}>
          {/* Map Toolbar */}
          <div className="bg-white border-b border-[#E0E0E0] px-3 py-2 flex items-center gap-2 overflow-x-auto flex-shrink-0">
            <button onClick={() => { setMapAddMode(mapAddMode === 'odb' ? null : 'odb'); setMapDrawPoints([]); setMapFormVisible(false); }}
              className={`flex-shrink-0 flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-bold border transition-all ${mapAddMode === 'odb' ? 'bg-[#0052CC] text-white border-[#0052CC]' : 'bg-white text-[#0052CC] border-[#0052CC]'}`}>
              <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor"><path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"/></svg> ODB Box
            </button>
            <button onClick={() => { setMapAddMode(mapAddMode === 'cable_route' ? null : 'cable_route'); setMapDrawPoints([]); setMapFormVisible(false); }}
              className={`flex-shrink-0 flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-bold border transition-all ${mapAddMode === 'cable_route' ? 'bg-[#E65100] text-white border-[#E65100]' : 'bg-white text-[#E65100] border-[#E65100]'}`}>
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M5 12h14M12 5l7 7-7 7"/></svg> Cable Route
            </button>
            <button onClick={() => { setMapAddMode(mapAddMode === 'user_home' ? null : 'user_home'); setMapDrawPoints([]); setMapFormVisible(false); }}
              className={`flex-shrink-0 flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-bold border transition-all ${mapAddMode === 'user_home' ? 'bg-[#006D3B] text-white border-[#006D3B]' : 'bg-white text-[#006D3B] border-[#006D3B]'}`}>
              <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor"><path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/><polyline points="9 22 9 12 15 12 15 22" fill="white"/></svg> User Home
            </button>
            <button onClick={loadMapObjects} className="flex-shrink-0 ml-auto bg-[#F5F6F8] text-[#666] px-2 py-1.5 rounded-full text-xs font-bold border border-[#E0E0E0]">
              {mapLoading ? 'Loading...' : 'Refresh'}
            </button>
          </div>

          {/* Mode instruction banner */}
          {mapAddMode && (
            <div className={`px-3 py-2 text-xs font-medium text-white flex-shrink-0 ${mapAddMode === 'odb' ? 'bg-[#0052CC]' : mapAddMode === 'cable_route' ? 'bg-[#E65100]' : 'bg-[#006D3B]'}`}>
              {mapAddMode === 'cable_route'
                ? `Tap map to draw cable route (${mapDrawPoints.length} points) — tap "Save Route" when done`
                : mapAddMode === 'odb' ? 'Tap on map to place ODB Box' : 'Tap on map to mark User Home'}
            </div>
          )}

          {/* Filter legend */}
          <div className="bg-white border-b border-[#E0E0E0] px-3 py-1.5 flex items-center gap-2 flex-shrink-0 overflow-x-auto">
            <span className="text-[10px] text-[#999] font-medium flex-shrink-0">Show:</span>
            {[{ k: 'odb', label: 'ODB', color: '#0052CC' }, { k: 'cable_route', label: 'Cable', color: '#E65100' }, { k: 'user_home', label: 'Homes', color: '#006D3B' }].map(f => (
              <button key={f.k} onClick={() => setMapFilter(prev => ({ ...prev, [f.k]: !prev[f.k] }))}
                className={`flex-shrink-0 flex items-center gap-1 text-[10px] font-bold px-2 py-0.5 rounded-full border transition-all`}
                style={{ borderColor: f.color, color: mapFilter[f.k] ? 'white' : f.color, background: mapFilter[f.k] ? f.color : 'white' }}>
                {f.label}
              </button>
            ))}
            {/* KMZ Infrastructure layer toggle */}
            <button onClick={() => setKmzVisible(v => !v)}
              className={`flex-shrink-0 flex items-center gap-1 text-[10px] font-bold px-2 py-0.5 rounded-full border transition-all`}
              style={{ borderColor: '#FF6B00', color: kmzVisible ? 'white' : '#FF6B00', background: kmzVisible ? '#FF6B00' : 'white' }}>
              <svg width="8" height="8" viewBox="0 0 24 24" fill="currentColor"><path d="M4 6h16M4 12h16M4 18h16" stroke="currentColor" strokeWidth="2.5" fill="none"/></svg>
              Infra
            </button>
            <span className="ml-auto text-[10px] text-[#999] flex-shrink-0">{mapObjects.length} obj</span>
          </div>

          {/* Box Search Bar */}
          <div className="bg-white border-b border-[#E0E0E0] px-3 py-2 flex-shrink-0 relative">
            <div className="flex items-center gap-2">
              <div className="flex-1 flex items-center gap-2 bg-[#F5F6F8] rounded-xl px-3 py-2 border border-[#E0E0E0] focus-within:border-[#0052CC]">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#999" strokeWidth="2"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>
                <input
                  value={boxSearchQuery}
                  onChange={e => {
                    const q = e.target.value;
                    setBoxSearchQuery(q);
                    if (q.trim().length >= 1) {
                      const results = mapObjects.filter(o => 
                        (o.obj_type === 'odb') && o.name && o.name.toLowerCase().includes(q.toLowerCase())
                      ).slice(0, 8);
                      setBoxSearchResults(results);
                    } else {
                      setBoxSearchResults([]);
                    }
                  }}
                  className="flex-1 bg-transparent text-xs outline-none text-[#333]"
                  placeholder="Search ODB box name..."
                />
                {(boxSearchQuery || routeTarget) && (
                  <button onClick={() => { setBoxSearchQuery(''); setBoxSearchResults([]); setRouteTarget(null); }} className="text-[#999] text-base leading-none">×</button>
                )}
              </div>
              {routeTarget && (
                <div className="flex items-center gap-1 bg-[#0052CC] text-white text-[10px] font-bold px-2 py-1.5 rounded-xl flex-shrink-0">
                  <svg width="10" height="10" viewBox="0 0 24 24" fill="currentColor"><path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z"/></svg>
                  {routeTarget.name}
                </div>
              )}
            </div>
            {/* Search Results Dropdown */}
            {boxSearchResults.length > 0 && (
              <div className="absolute left-3 right-3 top-full mt-0 bg-white rounded-xl shadow-xl border border-[#E0E0E0] z-[3000] overflow-hidden">
                {boxSearchResults.map(obj => (
                  <button key={obj.object_id}
                    onClick={() => {
                      setRouteTarget({ lat: obj.lat, lng: obj.lng, name: obj.name });
                      setBoxSearchQuery(obj.name);
                      setBoxSearchResults([]);
                    }}
                    className="w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-[#F5F6F8] active:bg-[#E3F2FD] border-b border-[#F0F0F0] last:border-0">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="#0052CC"><path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"/></svg>
                    <div>
                      <p className="text-xs font-bold text-[#333]">{obj.name}</p>
                      <p className="text-[10px] text-[#999]">📍 {obj.lat?.toFixed(5)}, {obj.lng?.toFixed(5)}</p>
                    </div>
                    {userMapLocation && (
                      <span className="ml-auto text-[10px] text-[#0052CC] font-bold">
                        {(Math.sqrt(Math.pow((obj.lat - userMapLocation.lat) * 111000, 2) + Math.pow((obj.lng - userMapLocation.lng) * 111000 * Math.cos(userMapLocation.lat * Math.PI / 180), 2)) / 1000).toFixed(2)} km
                      </span>
                    )}
                  </button>
                ))}
              </div>
            )}
          </div>
          {/* Leaflet Map */}
          <div className="flex-1 relative" style={{ minHeight: 0 }}>
            <MapContainer
              center={userMapLocation ? [userMapLocation.lat, userMapLocation.lng] : [16.8409, 96.1735]}
              zoom={14}
              style={{ width: '100%', height: '100%' }}
              zoomControl={false}
            >
              <TileLayer
                url="/gsat/?x={x}&y={y}&z={z}"
                maxZoom={20}
                attribution="&copy; Google Maps Satellite"
              />
              {/* Auto-fit map to KMZ routes when loaded */}
              {kmzVisible && <FitBoundsOnLoad routes={mapObjects.filter(o => o.obj_type === 'backbone_route' || o.obj_type === 'distribution_route')} />}
              {/* Live user location marker */}
              {userMapLocation && (
                <Marker
                  position={[userMapLocation.lat, userMapLocation.lng]}
                  icon={L.divIcon({ className: '', html: `<div style="width:20px;height:20px;background:#2563EB;border:3px solid white;border-radius:50%;box-shadow:0 0 0 4px rgba(37,99,235,0.3)"></div>`, iconSize: [20, 20], iconAnchor: [10, 10] })}
                >
                  <Tooltip permanent direction="top"><span style={{fontSize:'11px',fontWeight:'bold'}}>You are here</span></Tooltip>
                </Marker>
              )}
              {/* Route line from user to target ODB box */}
              {routeTarget && userMapLocation && (
                <>
                  <Polyline
                    positions={[[userMapLocation.lat, userMapLocation.lng], [routeTarget.lat, routeTarget.lng]]}
                    pathOptions={{ color: '#FF6B00', weight: 4, dashArray: '10 6', opacity: 0.9 }}
                  />
                  <Marker
                    position={[routeTarget.lat, routeTarget.lng]}
                    icon={L.divIcon({ className: '', html: `<div style="position:relative;width:36px;height:48px"><svg viewBox="0 0 36 48" xmlns="http://www.w3.org/2000/svg" style="width:36px;height:48px;filter:drop-shadow(0 4px 8px rgba(255,107,0,0.6))"><path d="M18 0C8.059 0 0 8.059 0 18c0 11.813 18 30 18 30s18-18.187 18-30C36 8.059 27.941 0 18 0z" fill="#FF6B00"/><circle cx="18" cy="18" r="10" fill="white" opacity="0.95"/><svg x="10" y="10" width="16" height="16" viewBox="0 0 24 24" fill="#FF6B00"><path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"/></svg></svg></div>`, iconSize: [36, 48], iconAnchor: [18, 48] })}
                  >
                    <Tooltip permanent direction="top">
                      <span style={{fontSize:'11px',fontWeight:'bold',color:'#FF6B00'}}>🎯 {routeTarget.name}</span>
                      {userMapLocation && <span style={{fontSize:'10px',display:'block',color:'#666'}}>{(Math.sqrt(Math.pow((routeTarget.lat - userMapLocation.lat) * 111000, 2) + Math.pow((routeTarget.lng - userMapLocation.lng) * 111000 * Math.cos(userMapLocation.lat * Math.PI / 180), 2)) / 1000).toFixed(2)} km away</span>}
                    </Tooltip>
                  </Marker>
                </>
              )}
              <MapClickHandler
                mapAddMode={mapAddMode}
                mapDrawPoints={mapDrawPoints}
                setMapDrawPoints={setMapDrawPoints}
                setMapFormPos={setMapFormPos}
                setMapFormVisible={setMapFormVisible}
              />

              {/* ODB Markers */}
              {mapFilter.odb && mapObjects.filter(o => o.obj_type === 'odb').map(o => (
                <Marker key={o.object_id} position={[o.lat, o.lng]}
                  icon={L.divIcon({ className: '', html: `<div style="position:relative;width:32px;height:42px"><svg viewBox="0 0 32 42" xmlns="http://www.w3.org/2000/svg" style="width:32px;height:42px;filter:drop-shadow(0 3px 6px rgba(0,82,204,0.5))"><path d="M16 0C7.163 0 0 7.163 0 16c0 10.5 16 26 16 26s16-15.5 16-26C32 7.163 24.837 0 16 0z" fill="#0052CC"/><circle cx="16" cy="16" r="9" fill="white" opacity="0.95"/><svg x="9" y="9" width="14" height="14" viewBox="0 0 24 24" fill="#0052CC"><path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"/></svg></svg></div>`, iconSize: [32, 42], iconAnchor: [16, 42] })}
                  eventHandlers={{ click: () => setMapSelectedObj(o) }}
                >
                  <Tooltip permanent={false} direction="top"><span className="text-xs font-bold">{o.name}</span></Tooltip>
                </Marker>
              ))}

              {/* User Home Markers */}
              {mapFilter.user_home && mapObjects.filter(o => o.obj_type === 'customer').map(o => (
                <Marker key={o.object_id} position={[o.lat, o.lng]}
                  icon={L.divIcon({ className: '', html: `<div style="position:relative;width:28px;height:38px"><svg viewBox="0 0 28 38" xmlns="http://www.w3.org/2000/svg" style="width:28px;height:38px;filter:drop-shadow(0 3px 5px rgba(0,109,59,0.5))"><path d="M14 0C6.268 0 0 6.268 0 14c0 9.5 14 24 14 24s14-14.5 14-24C28 6.268 21.732 0 14 0z" fill="#16A34A"/><circle cx="14" cy="14" r="8" fill="white" opacity="0.95"/><svg x="7" y="7" width="14" height="14" viewBox="0 0 24 24" fill="#16A34A"><path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/><polyline points="9 22 9 12 15 12 15 22"/></svg></svg></div>`, iconSize: [28, 38], iconAnchor: [14, 38] })}
                  eventHandlers={{ click: () => setMapSelectedObj(o) }}
                >
                  <Tooltip permanent={false} direction="top"><span className="text-xs">{o.name}</span></Tooltip>
                </Marker>
              ))}

              {/* Cable Routes (manually drawn) */}
              {mapFilter.cable_route && mapObjects.filter(o => o.obj_type === 'cable_route' && o.coordinates?.length >= 2).map(o => (
                <Polyline key={o.object_id}
                  positions={o.coordinates.map(c => [c.lat, c.lng])}
                  pathOptions={{ color: '#E65100', weight: 3, opacity: 0.85 }}
                  eventHandlers={{ click: () => setMapSelectedObj(o) }}
                />
              ))}

              {/* KMZ Infrastructure Routes - using native Leaflet API for reliable rendering */}
              <NativePolylines
                routes={mapObjects.filter(o => (o.obj_type === 'backbone_route' || o.obj_type === 'distribution_route') && (o.points?.length >= 2))}
                visible={kmzVisible}
              />

              {/* Drawing preview for cable route */}
              {mapAddMode === 'cable_route' && mapDrawPoints.length >= 2 && (
                <Polyline positions={mapDrawPoints.map(p => [p.lat, p.lng])} pathOptions={{ color: '#E65100', weight: 3, dashArray: '6 4', opacity: 0.7 }} />
              )}
              {mapAddMode === 'cable_route' && mapDrawPoints.map((p, i) => (
                <CircleMarker key={i} center={[p.lat, p.lng]} radius={5} pathOptions={{ color: '#E65100', fillColor: '#FFF', fillOpacity: 1, weight: 2 }} />
              ))}
            </MapContainer>

            {/* My Location button */}
            <button
              onClick={() => {
                if (userMapLocation) {
                  // Pan map to user location - trigger via state update
                  setUserMapLocation({ ...userMapLocation, _pan: Date.now() });
                } else if (navigator.geolocation) {
                  // Request GPS with high accuracy, silent on error - watchPosition will keep retrying
                  navigator.geolocation.getCurrentPosition(
                    (pos) => setUserMapLocation({ lat: pos.coords.latitude, lng: pos.coords.longitude }),
                    (err) => {
                      // Silent fail - watchPosition is already running in background
                      // If permission denied, show a toast-style message instead of alert
                      if (err.code === 1) {
                        const toast = document.createElement('div');
                        toast.innerHTML = 'GPS blocked. Go to: <b>Browser Settings → Site Settings → Location → Allow</b>';
                        toast.style.cssText = 'position:fixed;bottom:80px;left:50%;transform:translateX(-50%);background:rgba(220,38,38,0.95);color:white;padding:12px 20px;border-radius:12px;font-size:12px;z-index:9999;pointer-events:none;max-width:280px;text-align:center;line-height:1.5';
                        document.body.appendChild(toast);
                        setTimeout(() => toast.remove(), 5000);
                      }
                    },
                    { enableHighAccuracy: true, timeout: 15000, maximumAge: 0 }
                  );
                }
              }}
              className="absolute bottom-20 right-4 z-[1000] bg-white rounded-full w-10 h-10 flex items-center justify-center shadow-lg border border-[#E0E0E0] active:bg-[#F0F0F0]"
              title="My Location"
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke={userMapLocation ? '#0052CC' : '#999'} strokeWidth="2">
                <circle cx="12" cy="12" r="3"/>
                <path d="M12 2v3M12 19v3M2 12h3M19 12h3"/>
                <circle cx="12" cy="12" r="9" strokeDasharray="2 3"/>
              </svg>
            </button>
            {/* Cable route save button */}
            {mapAddMode === 'cable_route' && mapDrawPoints.length >= 2 && !mapFormVisible && (
              <button onClick={() => setMapFormVisible(true)}
                className="absolute bottom-4 left-1/2 -translate-x-1/2 z-[1000] bg-[#E65100] text-white px-5 py-2.5 rounded-full text-sm font-bold shadow-lg active:bg-[#BF360C]">
                Save Route ({mapDrawPoints.length} pts)
              </button>
            )}
          </div>

          {/* Add Point Form (ODB / User Home) */}
          {mapFormVisible && mapFormPos && mapAddMode !== 'cable_route' && (
            <div className="absolute bottom-16 left-4 right-4 z-[2000] bg-white rounded-2xl shadow-2xl p-4 border border-[#E0E0E0]">
              <p className="font-bold text-sm mb-3">{mapAddMode === 'odb' ? 'New ODB Box' : 'New User Home'}</p>
              <p className="text-[10px] text-[#999] mb-2">📍 {mapFormPos.lat.toFixed(5)}, {mapFormPos.lng.toFixed(5)}</p>
              <input value={mapForm.name} onChange={e => setMapForm({ ...mapForm, name: e.target.value })} className="w-full bg-[#F5F6F8] rounded-xl px-3 py-2 text-sm border border-[#E0E0E0] outline-none focus:border-[#0052CC] mb-2" placeholder={mapAddMode === 'odb' ? 'ODB Name (e.g. ODB-Hlaing-06)' : 'Customer Name / Address'} />
              {mapAddMode === 'odb' && <input value={mapForm.capacity} onChange={e => setMapForm({ ...mapForm, capacity: e.target.value })} type="number" className="w-full bg-[#F5F6F8] rounded-xl px-3 py-2 text-sm border border-[#E0E0E0] outline-none focus:border-[#0052CC] mb-2" placeholder="Port capacity (e.g. 16)" />}
              <input value={mapForm.description} onChange={e => setMapForm({ ...mapForm, description: e.target.value })} className="w-full bg-[#F5F6F8] rounded-xl px-3 py-2 text-sm border border-[#E0E0E0] outline-none focus:border-[#0052CC] mb-3" placeholder="Description (optional)" />
              <div className="flex gap-2">
                <button onClick={() => { setMapFormVisible(false); setMapFormPos(null); }} className="flex-1 bg-[#F5F6F8] text-[#666] rounded-xl py-2.5 text-sm font-bold">Cancel</button>
                <button onClick={mapSavePoint} className="flex-1 bg-[#0052CC] text-white rounded-xl py-2.5 text-sm font-bold active:bg-[#003D99]">Save to Map</button>
              </div>
            </div>
          )}

          {/* Cable Route Save Form */}
          {mapFormVisible && mapAddMode === 'cable_route' && (
            <div className="absolute bottom-16 left-4 right-4 z-[2000] bg-white rounded-2xl shadow-2xl p-4 border border-[#E0E0E0]">
              <p className="font-bold text-sm mb-3">Save Cable Route ({mapDrawPoints.length} points)</p>
              <input value={mapForm.name} onChange={e => setMapForm({ ...mapForm, name: e.target.value })} className="w-full bg-[#F5F6F8] rounded-xl px-3 py-2 text-sm border border-[#E0E0E0] outline-none focus:border-[#E65100] mb-2" placeholder="Cable route name (e.g. Cable-Hlaing-01)" />
              <input value={mapForm.description} onChange={e => setMapForm({ ...mapForm, description: e.target.value })} className="w-full bg-[#F5F6F8] rounded-xl px-3 py-2 text-sm border border-[#E0E0E0] outline-none focus:border-[#E65100] mb-3" placeholder="Description (optional)" />
              <div className="flex gap-2">
                <button onClick={() => { setMapFormVisible(false); setMapDrawPoints([]); setMapAddMode(null); }} className="flex-1 bg-[#F5F6F8] text-[#666] rounded-xl py-2.5 text-sm font-bold">Cancel</button>
                <button onClick={mapSaveCableRoute} className="flex-1 bg-[#E65100] text-white rounded-xl py-2.5 text-sm font-bold active:bg-[#BF360C]">Save Route</button>
              </div>
            </div>
          )}

          {/* Selected Object Detail Panel */}
          {mapSelectedObj && (
            <div className="absolute bottom-16 left-4 right-4 z-[2000] bg-white rounded-2xl shadow-2xl p-4 border border-[#E0E0E0]">
              <div className="flex items-start justify-between mb-2">
                <div>
                  <p className="font-bold text-sm">{mapSelectedObj.name}</p>
                  <p className="text-[10px] text-[#999] capitalize">{mapSelectedObj.obj_type?.replace('_', ' ')}</p>
                </div>
                <button onClick={() => setMapSelectedObj(null)} className="text-[#999] text-lg leading-none">×</button>
              </div>
              {mapSelectedObj.description && <p className="text-xs text-[#666] mb-2">{mapSelectedObj.description}</p>}
              {mapSelectedObj.obj_type === 'odb' && (
                <div className="flex gap-3 text-xs mb-2">
                  <span className="bg-[#E3F2FD] text-[#0052CC] px-2 py-0.5 rounded-full font-bold">Capacity: {mapSelectedObj.capacity}</span>
                  <span className="bg-[#E8F5E9] text-[#006D3B] px-2 py-0.5 rounded-full font-bold">Used: {mapSelectedObj.used_ports || 0}</span>
                  <span className="bg-[#FFF8E1] text-[#F57F17] px-2 py-0.5 rounded-full font-bold">Free: {(mapSelectedObj.capacity || 0) - (mapSelectedObj.used_ports || 0)}</span>
                </div>
              )}
              {mapSelectedObj.lat && <p className="text-[10px] text-[#999] mb-2">📍 {mapSelectedObj.lat?.toFixed(5)}, {mapSelectedObj.lng?.toFixed(5)}</p>}
              {mapSelectedObj.added_by_mobile && <p className="text-[10px] text-[#0052CC] mb-2">📱 Added from mobile{mapSelectedObj.added_from_task ? ` (Task: ${mapSelectedObj.added_from_task})` : ''}</p>}
              <p className="text-[10px] text-[#999] mb-3">By: {mapSelectedObj.created_by} · {mapSelectedObj.created_at ? new Date(mapSelectedObj.created_at).toLocaleDateString() : ''}</p>
              <button onClick={() => mapDeleteObject(mapSelectedObj.object_id)} className="w-full bg-[#FFEBEE] text-[#BA1A1A] rounded-xl py-2 text-xs font-bold active:bg-[#FFCDD2]">Delete from Map</button>
            </div>
          )}
        </div>
      )}

      {/* Bottom Nav */}
      <div className="fixed bottom-0 left-0 right-0 mobile-app-root bg-white border-t border-[#E0E0E0] flex items-center justify-around py-2 z-50">
        {[
          { icon: <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor"><path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/></svg>, label: 'Home', s: 'home' },
          { icon: <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="5" y="2" width="14" height="20" rx="2"/><line x1="9" y1="7" x2="15" y2="7"/><line x1="9" y1="11" x2="15" y2="11"/><line x1="9" y1="15" x2="12" y2="15"/></svg>, label: 'Jobs', s: 'jobs' },
          { icon: <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10"/><line x1="2" y1="12" x2="22" y2="12"/><path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/></svg>, label: 'Map', s: 'map' },
          { icon: <Database size={20} weight="bold" />, label: 'ODB', s: 'odb-check' },
          ...(isAdminOrSupervisor ? [{ icon: <MapPin size={20} weight="fill" />, label: 'Team', s: 'team-movement' }] : []),
          { icon: <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="23 4 23 10 17 10"/><polyline points="1 20 1 14 7 14"/><path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15"/></svg>, label: pendingCount > 0 ? `Sync (${pendingCount})` : 'Sync', s: 'sync' }
        ].map(n => (
          <button key={n.s} onClick={() => { setScreen(n.s); setSelectedTask(null); setMapAddMode(null); setMapDrawPoints([]); setMapFormVisible(false); }} className={`flex flex-col items-center gap-0.5 px-3 py-1 ${screen === n.s ? 'text-[#0052CC]' : 'text-[#999]'}`}>
            {n.icon}
            <span className="text-[10px] font-medium">{n.label}</span>
          </button>
        ))}
        {/* Logout button in bottom nav */}
        <button onClick={() => { logout(); navigate('/mobile'); }} className="flex flex-col items-center gap-0.5 px-3 py-1 text-red-500 active:scale-95 transition-all">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/><polyline points="16 17 21 12 16 7"/><line x1="21" y1="12" x2="9" y2="12"/></svg>
          <span className="text-[10px] font-medium">Logout</span>
        </button>
      </div>
    </div>
  );
}
